// src/pages/Billing.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { isIOSApp } from "../utils/platform";
import { useCart } from "../context/CartContext";

import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const FREE_LIMIT_BYTES = 250 * 1024 * 1024; // 250 MB fallback when backend quota is missing

const MONTHLY_PLAN_TYPES = ["monthly_essential", "monthly_pro", "monthly_elite"];

const MONTHLY_LABELS = {
  monthly_essential: "Monthly Essential",
  monthly_pro: "Monthly Pro",
  monthly_elite: "Monthly Elite",
};

// ✅ Storage caps for marketing clarity (Option A)
const STORAGE_CAPS = {
  free: "250 MB",
  monthly_essential: "5 GB",
  monthly_pro: "25 GB",
  monthly_elite: "100 GB",
};

const SERVICE_LABELS = {
  monthly_essential: "Monthly Essential",
  monthly_pro: "Monthly Pro",
  monthly_elite: "Monthly Elite",
  case_analyzer_250: "Case Analyzer – up to 250 pages",
  case_analyzer_500: "Case Analyzer – up to 500 pages",
  case_analyzer_750: "Case Analyzer – up to 750 pages",
  case_analyzer_1000: "Case Analyzer – up to 1,000 pages",
  case_analyzer_1250: "Case Analyzer – up to 1,250 pages",
  case_analyzer_1500: "Case Analyzer – up to 1,500 pages",
  contract_insight_25: "Contract Insight – up to 25 pages",
  contract_insight_50: "Contract Insight – up to 50 pages",
  contract_insight_75: "Contract Insight – up to 75 pages",
  contract_insight_150: "Contract Insight – up to 150 pages",
  contract_insight_250: "Contract Insight – up to 250 pages",
  doc_writer_1: "Doc Writer – 1",
  doc_writer_5: "Doc Writer – 5",
  doc_writer_15: "Doc Writer – 15",
  smart_alert_6: "Smart Alert – 6",
  smart_alert_12: "Smart Alert – 12",
  smart_alert_24: "Smart Alert – 24",
  vision_assist_15: "Vision Assist – 15",
  vision_assist_30: "Vision Assist – 30",
  vision_assist_60: "Vision Assist – 60",
};

const CONTRACT_INSIGHT_PAGE_TIERS = [25, 50, 75, 150, 250];
const CASE_ANALYZER_PAGE_TIERS = [250, 500, 750, 1000, 1250, 1500];

const formatPageTierLabel = (tier) => {
  const n = Number(tier || 0);
  if (!Number.isFinite(n) || n <= 0) return "Page package";
  return `Up to ${n.toLocaleString()} pages`;
};

const readPackageRemaining = (pkg) => {
  if (typeof pkg === "number" && Number.isFinite(pkg)) return Math.max(0, pkg);
  if (!pkg || typeof pkg !== "object") return 0;

  const candidates = [
    pkg.remaining_reports,
    pkg.remaining_report,
    pkg.remaining_docs,
    pkg.remaining_documents,
    pkg.remaining,
    pkg.count,
    pkg.qty,
    pkg.quantity,
    pkg.reports,
    pkg.docs,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
};

const readPackageTier = (pkg, fallbackKey = "") => {
  if (pkg && typeof pkg === "object") {
    const candidates = [pkg.page_tier, pkg.pageTier, pkg.max_pages, pkg.maxPages, pkg.pages, pkg.tier];
    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }

  const match = String(fallbackKey || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const normalizePagePackages = ({
  bucket,
  packageKey,
  tiers,
  includeZeroRows = true,
}) => {
  const counts = new Map();
  const raw = bucket && typeof bucket === "object" ? bucket[packageKey] : null;

  if (Array.isArray(raw)) {
    raw.forEach((pkg) => {
      const tier = readPackageTier(pkg);
      if (!tier) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(pkg));
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const tier = readPackageTier(value, key);
      if (!tier) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(value));
    });
  }

  return tiers
    .map((tier) => {
      const remaining = Math.max(0, Number(counts.get(tier) || 0));
      return {
        page_tier: tier,
        label: formatPageTierLabel(tier),
        remaining,
        value: `${remaining} remaining`,
      };
    })
    .filter((row) => includeZeroRows || row.remaining > 0);
};

const hasPositivePackage = (rows) => Array.isArray(rows) && rows.some((row) => Number(row?.remaining || 0) > 0);

const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const pickFirstBool = (...values) => {
  for (const value of values) {
    if (typeof value === "boolean") return value;
  }
  return false;
};

const pickFirstArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }
  return [];
};

const priceToCents = (price) => {
  const n = Number(String(price || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

const pickEntitlementNumber = (bucket, ...keys) => {
  if (!bucket || typeof bucket !== "object") return 0;

  for (const key of keys) {
    const value = bucket?.[key];
    if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, value);
  }

  return 0;
};

const formatVisionTime = (minutes) => {
  const raw = Number(minutes || 0);
  const totalSeconds = Number.isFinite(raw) ? Math.max(0, Math.round(raw * 60)) : 0;
  const wholeMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${wholeMinutes}:${String(seconds).padStart(2, "0")}`;
};

const Billing = () => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  // NOTE: still used internally to prevent purchase actions when in iOS app,
  // but we do NOT display iOS-specific messaging on the page.
  const [notAllowed, setNotAllowed] = useState(false);

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const navigate = useNavigate();
  const { cartCount, addToCart, isInCart } = useCart();
  const [cartNotice, setCartNotice] = useState(null);

  // ✅ Customer billing history state
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [billingHistory, setBillingHistory] = useState({ items: [], limit: 3, has_more: false });
  const [billingHistoryLimit, setBillingHistoryLimit] = useState(3);
  const [billingHistoryLoading, setBillingHistoryLoading] = useState(false);
  const [billingHistoryError, setBillingHistoryError] = useState("");
  const [downloadingBillingId, setDownloadingBillingId] = useState("");

  // ✅ Package row expansion state for page-tier services
  const [expandedPackageRows, setExpandedPackageRows] = useState({});

  // ✅ pricing mode: show marketing even when logged out
  const [pricingMode, setPricingMode] = useState(false);

  // ✅ Update-card (embedded Stripe PaymentElement)
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");

  // ✅ Auto-renew toggle UI state
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewError, setRenewError] = useState("");

  // ✅ Influencer earnings state
  const [influencerEarnings, setInfluencerEarnings] = useState(null);
  const [influencerLoading, setInfluencerLoading] = useState(false);
  const [influencerError, setInfluencerError] = useState("");
  const [expandedStatementMonth, setExpandedStatementMonth] = useState("");
  const [statementDownloadState, setStatementDownloadState] = useState({
    type: "",
    message: "",
    month_key: "",
  });
  const [downloadingStatementMonth, setDownloadingStatementMonth] = useState("");

  // ✅ Stripe Connect onboarding state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectStatusLoading, setConnectStatusLoading] = useState(false);
  const [connectSetupLoading, setConnectSetupLoading] = useState(false);
  const [connectSetupError, setConnectSetupError] = useState("");
  const [connectSetupNotice, setConnectSetupNotice] = useState("");
  const [activeAddOnGroup, setActiveAddOnGroup] = useState("Case Analyzer");
  const addOnTabsRef = useRef(null);
  const addOnTabRefs = useRef({});

  // Match About.jsx behavior: center heading on small screens
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const refreshStripeConnectStatus = async ({
    token: providedToken,
    influencerUid: providedInfluencerUid,
    silent = false,
  } = {}) => {
    const uid = providedInfluencerUid || billing?.influencer_uid || null;
    if (!uid) {
      setConnectStatus(null);
      return null;
    }

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");

      const token = providedToken || (await user.getIdToken());

      if (!silent) {
        setConnectSetupError("");
        setConnectSetupNotice("");
        setConnectStatusLoading(true);
      }

      const res = await fetch(`${HOST}/connect/refresh-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ influencer_uid: uid }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setConnectStatus(data?.connect_status || null);
      return data?.connect_status || null;
    } catch (err) {
      console.error("Stripe Connect refresh error:", err);
      if (!silent) {
        setConnectSetupError(err?.message || "Unable to refresh Stripe status right now.");
      }
      return null;
    } finally {
      if (!silent) {
        setConnectStatusLoading(false);
      }
    }
  };

  const refreshBilling = async () => {
    const user = getAuth().currentUser;

    // ✅ logged out -> PRICING MODE (no redirect)
    if (!user) {
      setPricingMode(true);
      setBilling({
        plan_type: "free",
        is_active: false,
        remaining_questions: null,
        remaining_alerts: null,
        expires_at: null,
        file_vault_expires_at: null,
        smart_alert_expires_at: null,
        quota_limit: FREE_LIMIT_BYTES,
        quota_used: 0,
        doc_writer_unlimited: false,
      });
      setInfluencerEarnings(null);
      setInfluencerError("");
      setExpandedStatementMonth("");
      setStatementDownloadState({ type: "", message: "", month_key: "" });
      setDownloadingStatementMonth("");
      setConnectStatus(null);
      setConnectSetupError("");
      setConnectSetupNotice("");
      setShowBillingHistory(false);
      setBillingHistory({ items: [], limit: 3, has_more: false });
      setBillingHistoryLimit(3);
      setBillingHistoryError("");
      setDownloadingBillingId("");
      setLoading(false);
      return;
    }

    setPricingMode(false);

    // iOS in-app: mark that purchases are not allowed, but still fetch read-only billing
    if (isIOSApp()) {
      setNotAllowed(true);
    } else {
      setNotAllowed(false);
    }

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${HOST}/user/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setBilling(data);

      const shouldFetchInfluencer = !!data?.is_influencer && !!data?.influencer_uid;

      if (shouldFetchInfluencer) {
        setInfluencerLoading(true);
        setInfluencerError("");
        try {
          const [earningsRes] = await Promise.all([
            fetch(
              `${HOST}/user/influencer-earnings?influencer_uid=${encodeURIComponent(data.influencer_uid)}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            ),
            refreshStripeConnectStatus({
              token,
              influencerUid: data.influencer_uid,
              silent: true,
            }),
          ]);

          const earningsData = await earningsRes.json().catch(() => ({}));
          if (!earningsRes.ok) {
            throw new Error(earningsData?.error || `HTTP ${earningsRes.status}`);
          }
          setInfluencerEarnings(earningsData);
        } catch (earnErr) {
          console.error("Influencer earnings fetch error:", earnErr);
          setInfluencerEarnings(null);
          setInfluencerError(earnErr?.message || "Unable to load your earnings.");
        } finally {
          setInfluencerLoading(false);
        }
      } else {
        setInfluencerEarnings(null);
        setInfluencerError("");
        setExpandedStatementMonth("");
        setStatementDownloadState({ type: "", message: "", month_key: "" });
        setDownloadingStatementMonth("");
        setConnectStatus(null);
      }
    } catch (err) {
      console.error("Billing fetch error:", err);
      setBilling(null);
      setInfluencerEarnings(null);
      setInfluencerError("");
      setExpandedStatementMonth("");
      setStatementDownloadState({ type: "", message: "", month_key: "" });
      setDownloadingStatementMonth("");
      setConnectStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAndFetch = async () => {
      setLoading(true);
      await refreshBilling();
    };
    checkAndFetch();
  }, [navigate]);

  const safeBilling = billing || {};
  const plan_type = safeBilling.plan_type ?? "";
  const expires_at = safeBilling.expires_at ?? null;
  const is_active = !!safeBilling.is_active;
  const remaining_questions = safeBilling.remaining_questions ?? null;
  const remaining_alerts = safeBilling.remaining_alerts ?? null;
  const file_vault_expires_at = safeBilling.file_vault_expires_at ?? null;
  const smart_alert_expires_at = safeBilling.smart_alert_expires_at ?? null;
  const quota_limit = safeBilling.quota_limit ?? null;
  const quota_used = safeBilling.quota_used ?? null;
  const doc_writer_unlimited = !!safeBilling.doc_writer_unlimited;

  // ✅ Influencer info from billing doc
  const isInfluencer = !!safeBilling.is_influencer;
  const influencerUid = safeBilling.influencer_uid ?? null;
  const influencerStatus = safeBilling.influencer_status ?? null;

  // ✅ Stripe Connect info (backend refresh-status is primary source of truth)
  const stripeConnectAccountId = pickFirstString(
    connectStatus?.stripe_connect_account_id,
    safeBilling.stripe_connect_account_id,
    influencerEarnings?.stripe_connect_account_id
  );

  const stripeConnectStatusRaw = pickFirstString(
    connectStatus?.stripe_connect_status,
    safeBilling.stripe_connect_status,
    influencerEarnings?.stripe_connect_status
  ).toLowerCase();

  const stripeConnectUiStatusRaw = pickFirstString(
    connectStatus?.stripe_connect_ui_status,
    safeBilling.stripe_connect_ui_status,
    influencerEarnings?.stripe_connect_ui_status
  ).toLowerCase();

  const stripeConnectUiStatusMessage = pickFirstString(
    connectStatus?.stripe_connect_ui_status_message,
    safeBilling.stripe_connect_ui_status_message,
    influencerEarnings?.stripe_connect_ui_status_message
  );

  const stripeConnectDetailsSubmitted = pickFirstBool(
    connectStatus?.stripe_connect_details_submitted,
    safeBilling.stripe_connect_details_submitted,
    influencerEarnings?.stripe_connect_details_submitted
  );

  const stripeConnectChargesEnabled = pickFirstBool(
    connectStatus?.stripe_connect_charges_enabled,
    safeBilling.stripe_connect_charges_enabled,
    influencerEarnings?.stripe_connect_charges_enabled
  );

  const stripeConnectPayoutsEnabled = pickFirstBool(
    connectStatus?.stripe_connect_payouts_enabled,
    safeBilling.stripe_connect_payouts_enabled,
    influencerEarnings?.stripe_connect_payouts_enabled
  );

  const stripeConnectCurrentlyDue = pickFirstArray(
    connectStatus?.stripe_connect_currently_due,
    safeBilling.stripe_connect_currently_due,
    influencerEarnings?.stripe_connect_currently_due
  );

  const stripeConnectPastDue = pickFirstArray(
    connectStatus?.stripe_connect_past_due,
    safeBilling.stripe_connect_past_due,
    influencerEarnings?.stripe_connect_past_due
  );

  const stripeConnectPendingVerification = pickFirstArray(
    connectStatus?.stripe_connect_pending_verification,
    safeBilling.stripe_connect_pending_verification,
    influencerEarnings?.stripe_connect_pending_verification
  );

  const stripeConnectEventuallyDue = pickFirstArray(
    connectStatus?.stripe_connect_eventually_due,
    safeBilling.stripe_connect_eventually_due,
    influencerEarnings?.stripe_connect_eventually_due
  );

  const stripeConnectFutureCurrentlyDue = pickFirstArray(
    connectStatus?.stripe_connect_future_currently_due,
    safeBilling.stripe_connect_future_currently_due,
    influencerEarnings?.stripe_connect_future_currently_due
  );

  const stripeConnectDisabledReason = pickFirstString(
    connectStatus?.stripe_connect_requirements_disabled_reason,
    safeBilling.stripe_connect_requirements_disabled_reason,
    influencerEarnings?.stripe_connect_requirements_disabled_reason
  );

  const stripeConnectLastSyncedAt = pickFirstString(
    connectStatus?.stripe_connect_last_synced_at,
    safeBilling.stripe_connect_last_synced_at,
    influencerEarnings?.stripe_connect_last_synced_at
  );

  // ✅ Monthly allowances (backend fields)
  const doc_writer_remaining_docs = safeBilling.doc_writer_remaining_docs ?? null;
  const contract_insight_remaining_docs = safeBilling.contract_insight_remaining_docs ?? null;
  const case_analyzer_remaining_reports = safeBilling.case_analyzer_remaining_reports ?? null;
  const vision_assist_remaining_minutes = safeBilling.vision_assist_remaining_minutes ?? null;

  // ✅ Document Intelligence
  // Free uses a lifetime entitlement. Paid monthly plans use monthly_entitlements.
  const document_intelligence_lifetime_limit =
    safeBilling.document_intelligence_lifetime_limit ?? 0;
  const document_intelligence_lifetime_remaining =
    safeBilling.document_intelligence_lifetime_remaining ?? 0;

  // ✅ Future-ready entitlement buckets. Backend can return these once monthly and add-on credits are split.
  const monthlyEntitlements = safeBilling.monthly_entitlements || safeBilling.monthlyEntitlements || {};
  const addonEntitlements = safeBilling.addon_entitlements || safeBilling.addonEntitlements || safeBilling.purchased_addons || {};

  const monthlyDocWriterRemaining = pickEntitlementNumber(monthlyEntitlements, "doc_writer_remaining_docs", "doc_writer", "docWriter");
  const monthlyContractInsightRemaining = pickEntitlementNumber(monthlyEntitlements, "contract_insight_remaining_docs", "contract_insight", "contractInsight");
  const monthlyCaseAnalyzerRemaining = pickEntitlementNumber(monthlyEntitlements, "case_analyzer_remaining_reports", "case_analyzer", "caseAnalyzer");
  const monthlyVisionAssistRemaining = pickEntitlementNumber(monthlyEntitlements, "vision_assist_remaining_minutes", "vision_assist", "visionAssist");
  const monthlyDocumentIntelligenceRemaining = pickEntitlementNumber(
    monthlyEntitlements,
    "document_intelligence_remaining_analyses",
    "document_intelligence",
    "documentIntelligence"
  );
  const monthlySmartAlertRemaining = pickEntitlementNumber(
    monthlyEntitlements,
    "smart_alert_remaining_alerts",
    "smart_alert_remaining",
    "smart_alert",
    "smartAlert"
  );

  const monthlyContractInsightPackages = normalizePagePackages({
    bucket: monthlyEntitlements,
    packageKey: "contract_insight_page_packages",
    tiers: CONTRACT_INSIGHT_PAGE_TIERS,
    includeZeroRows: true,
  });

  const monthlyCaseAnalyzerPackages = normalizePagePackages({
    bucket: monthlyEntitlements,
    packageKey: "case_analyzer_page_packages",
    tiers: CASE_ANALYZER_PAGE_TIERS,
    includeZeroRows: true,
  });

  const addonCaseAnalyzerRemaining = pickEntitlementNumber(addonEntitlements, "case_analyzer_remaining_reports", "case_analyzer", "caseAnalyzer");
  const addonContractInsightRemaining = pickEntitlementNumber(addonEntitlements, "contract_insight_remaining_docs", "contract_insight", "contractInsight");
  const addonContractInsightPackages = normalizePagePackages({
    bucket: addonEntitlements,
    packageKey: "contract_insight_page_packages",
    tiers: CONTRACT_INSIGHT_PAGE_TIERS,
    includeZeroRows: true,
  });

  const addonCaseAnalyzerPackages = normalizePagePackages({
    bucket: addonEntitlements,
    packageKey: "case_analyzer_page_packages",
    tiers: CASE_ANALYZER_PAGE_TIERS,
    includeZeroRows: true,
  });

  const hasAddonContractInsightPackages = hasPositivePackage(addonContractInsightPackages) || addonContractInsightRemaining > 0;
  const hasAddonCaseAnalyzerPackages = hasPositivePackage(addonCaseAnalyzerPackages) || addonCaseAnalyzerRemaining > 0;

  const addonDocWriterRemaining = pickEntitlementNumber(addonEntitlements, "doc_writer_remaining_docs", "doc_writer", "docWriter");
  const addonSmartAlertRemaining = pickEntitlementNumber(
    addonEntitlements,
    "smart_alert_remaining_alerts",
    "smart_alert_remaining",
    "smart_alert",
    "smartAlert",
    "remaining_alerts"
  );
  const addonVisionAssistRemaining = pickEntitlementNumber(addonEntitlements, "vision_assist_remaining_minutes", "vision_assist", "visionAssist");

  // ✅ Subscription + card display fields (optional, from backend)
  const stripe_subscription_id = safeBilling.stripe_subscription_id ?? null;
  const cancel_at_period_end = safeBilling.cancel_at_period_end ?? null;
  const current_period_end = safeBilling.current_period_end ?? null;
  const subscription_status = safeBilling.subscription_status ?? null;

  const card_brand = safeBilling.card_brand ?? null;
  const card_last4 = safeBilling.card_last4 ?? null;
  const card_exp_month = safeBilling.card_exp_month ?? null;
  const card_exp_year = safeBilling.card_exp_year ?? null;

  const isMonthlyCore = MONTHLY_PLAN_TYPES.includes(plan_type);
  const hasDocWriterUnlimited = !!doc_writer_unlimited;
  const monthlyPlanPurchaseLocked = Boolean(!pricingMode && isMonthlyCore && is_active);
  const hasSmartAlertIncluded = Boolean(is_active && plan_type === "monthly_elite");
  const canShowSmartAlertAddons = !hasSmartAlertIncluded;

  // ✅ Hide internal/unsupported plan identifiers from display
  const isHiddenPlanType = ["unlimited", "monthly_unlimited", "session", "monthly"].includes(plan_type);
  const prettyPlanType =
    plan_type === "free"
      ? "Free Plan"
      : plan_type === "trial_7d"
      ? "7-Day Trial"
      : isHiddenPlanType
      ? "Custom Plan"
      : MONTHLY_LABELS[plan_type] || plan_type;

  // Presentation only: Free is an active plan even though backend is_active is
  // reserved for time-limited/paid plan activity.
  const planStatusLabel =
    plan_type === "free"
      ? "Active"
      : (plan_type === "trial_7d" || isMonthlyCore) && !is_active
      ? "Expired"
      : is_active
      ? "Active"
      : "Inactive";

  const planStatusIsActive = plan_type === "free" || is_active;
  const showRenewalMeta = plan_type !== "free";

  const formatStorage = (bytes) => {
    if (bytes == null) return null;
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      const gb = mb / 1024;
      return gb >= 10 ? `${gb.toFixed(0)} GB` : `${gb.toFixed(1)} GB`;
    }
    return mb >= 10 ? `${mb.toFixed(0)} MB` : `${mb.toFixed(1)} MB`;
  };

  const formatMoney = (value) => {
    const amount = Number(value || 0);
    return `$${amount.toFixed(2)}`;
  };

  const formatMonthKey = (monthKey) => {
    if (!monthKey || typeof monthKey !== "string") return "Monthly statement";
    const [year, month] = monthKey.split("-");
    if (!year || !month) return monthKey;
    const date = new Date(Number(year), Number(month) - 1, 1);
    if (Number.isNaN(date.getTime())) return monthKey;
    return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  };

  const prettifyPayoutStatus = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "—";
    if (raw === "unpaid") return "Pending";
    return raw.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const normalizedInfluencerBadge = useMemo(() => {
    const raw = String(influencerStatus || "").trim().toLowerCase();
    if (!raw) return null;
    return raw === "active" ? "Active" : raw.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  }, [influencerStatus]);

  const stripeConnectBadge = useMemo(() => {
    if (stripeConnectUiStatusRaw === "ready_for_payouts") return "Payouts Active";
    if (stripeConnectUiStatusRaw === "action_required") return "Action Required";
    if (stripeConnectUiStatusRaw === "under_review") return "Under Review";
    if (stripeConnectUiStatusRaw === "setup_incomplete") return "Setup Incomplete";
    if (stripeConnectUiStatusRaw === "restricted") return "Restricted";
    if (stripeConnectDetailsSubmitted) return "Submitted";
    if (stripeConnectStatusRaw) {
      return stripeConnectStatusRaw.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
    }
    if (stripeConnectAccountId) return "Setup Needed";
    return "Not Started";
  }, [
    stripeConnectAccountId,
    stripeConnectDetailsSubmitted,
    stripeConnectStatusRaw,
    stripeConnectUiStatusRaw,
  ]);

  const stripeConnectDescription = useMemo(() => {
    if (stripeConnectUiStatusMessage) return stripeConnectUiStatusMessage;

    if (stripeConnectPayoutsEnabled) {
      return "Your Stripe payout account is active and ready to receive payouts.";
    }
    if (stripeConnectDetailsSubmitted) {
      return stripeConnectCurrentlyDue.length
        ? "Stripe still needs a few items before payouts can be enabled."
        : "Your onboarding information has been submitted. Stripe is still reviewing your payout setup.";
    }
    if (stripeConnectAccountId) {
      return "Your payout account is ready to finish. Complete Stripe onboarding to receive payouts.";
    }
    return "Once approved, you can complete Stripe onboarding here to receive influencer payouts.";
  }, [
    stripeConnectAccountId,
    stripeConnectCurrentlyDue.length,
    stripeConnectDetailsSubmitted,
    stripeConnectPayoutsEnabled,
    stripeConnectUiStatusMessage,
  ]);

  const stripeConnectActionLabel = useMemo(() => {
    if (!isInfluencer || !influencerUid) return "";
    if (stripeConnectUiStatusRaw === "ready_for_payouts") return "Update payout details";
    if (stripeConnectUiStatusRaw === "action_required") return "Resume Stripe setup";
    if (stripeConnectUiStatusRaw === "under_review") return "Review payout details";
    if (stripeConnectUiStatusRaw === "setup_incomplete") return "Complete Stripe payout setup";
    if (stripeConnectAccountId) return "Resume Stripe setup";
    return "Start Stripe payout setup";
  }, [influencerUid, isInfluencer, stripeConnectAccountId, stripeConnectUiStatusRaw]);

  const earningsMonths = useMemo(() => {
    const payload = influencerEarnings || {};

    const directMonths = Array.isArray(payload?.months) ? payload.months : [];
    const altStatements = Array.isArray(payload?.statements) ? payload.statements : [];
    const altItems = Array.isArray(payload?.items) ? payload.items : [];
    const nestedMonths = Array.isArray(payload?.earnings?.months) ? payload.earnings.months : [];
    const nestedStatements = Array.isArray(payload?.earnings?.statements) ? payload.earnings.statements : [];

    const combined = [
      ...directMonths,
      ...altStatements,
      ...altItems,
      ...nestedMonths,
      ...nestedStatements,
    ].filter(Boolean);

    const normalized = combined.map((month) => ({
      ...month,
      month_key:
        month?.month_key ||
        month?.monthKey ||
        month?.statement_month_key ||
        month?.statementMonthKey ||
        "",
      totals: month?.totals || {},
      payout: month?.payout || {},
      breakdown: month?.breakdown || {},
      entries: Array.isArray(month?.entries) ? month.entries : [],
    }));

    return normalized.sort((a, b) =>
      String(b?.month_key || "").localeCompare(String(a?.month_key || ""))
    );
  }, [influencerEarnings]);

  useEffect(() => {
    if (!earningsMonths.length) {
      setExpandedStatementMonth("");
      return;
    }

    setExpandedStatementMonth((prev) => {
      if (prev && earningsMonths.some((month) => month?.month_key === prev)) {
        return prev;
      }
      return earningsMonths[0]?.month_key || "";
    });
  }, [earningsMonths]);

  const currentEarningsMonth = useMemo(() => {
    if (earningsMonths.length > 0) {
      return earningsMonths[0];
    }

    const payload = influencerEarnings || {};
    const current =
      payload?.current_month ||
      payload?.currentMonth ||
      payload?.latest_month ||
      payload?.latestMonth ||
      payload?.month ||
      payload?.statement ||
      payload?.current_statement ||
      payload?.currentStatement ||
      null;

    if (!current || typeof current !== "object") {
      return null;
    }

    return {
      ...current,
      month_key:
        current?.month_key ||
        current?.monthKey ||
        current?.statement_month_key ||
        current?.statementMonthKey ||
        "",
      totals: current?.totals || {},
      payout: current?.payout || {},
      breakdown: current?.breakdown || {},
      entries: Array.isArray(current?.entries) ? current.entries : [],
    };
  }, [earningsMonths, influencerEarnings]);

  const currentEarningsTotals = currentEarningsMonth?.totals || {};
  const influencerPromoCode =
    influencerEarnings?.promo_code ||
    influencerEarnings?.code ||
    influencerEarnings?.influencer?.promo_code ||
    influencerEarnings?.influencer?.code ||
    influencerEarnings?.profile?.promo_code ||
    influencerEarnings?.profile?.code ||
    safeBilling?.influencer_promo_code ||
    safeBilling?.promo_code ||
    "—";

  const isFutureIso = (iso) => {
    if (!iso) return false;
    try {
      return new Date(iso) > new Date();
    } catch {
      return false;
    }
  };

  const smartAlertActive = isFutureIso(smart_alert_expires_at);
  const smartAlertRemaining = typeof remaining_alerts === "number" ? remaining_alerts : null;

  const hasSubscription = Boolean(stripe_subscription_id);
  const autoRenewOn = hasSubscription ? !Boolean(cancel_at_period_end) : null;
  const renewalIso = current_period_end || expires_at || null;
  const renewalLabel = renewalIso ? new Date(renewalIso).toLocaleDateString() : "—";

  const paymentMethodLabel = card_last4
    ? `${card_brand ? `${String(card_brand).toUpperCase()} ` : ""}•••• ${card_last4}${
        card_exp_month && card_exp_year
          ? ` • ${String(card_exp_month).padStart(2, "0")}/${String(card_exp_year).slice(-2)}`
          : ""
      }`
    : "—";

  // Source of truth = backend quota_limit/quota_used.
  // Logged-out pricing mode already supplies its own marketing-only Free quota.
  // Logged-in billing must never manufacture a Free quota.
  const effectiveQuotaLimit = typeof quota_limit === "number" ? quota_limit : 0;
  const effectiveQuotaUsed = typeof quota_used === "number" ? quota_used : 0;

  const storageTotal = formatStorage(effectiveQuotaLimit);
  const storageUsed = formatStorage(effectiveQuotaUsed);
  const storageRemaining = formatStorage(Math.max(effectiveQuotaLimit - effectiveQuotaUsed, 0));

  const renderUsage = () => {
    if (plan_type === "free") {
      return (
        <p style={stylesText.metaLine}>
          <strong>Free Questions:</strong> {remaining_questions ?? "N/A"} remaining
        </p>
      );
    } else if (plan_type === "trial_7d") {
      return (
        <p style={stylesText.metaLine}>
          <strong>Trial Bundle:</strong> 30 questions, 3 Doc Writer documents, 3 Contract Insight
          documents, and 10 minutes of Vision Assist.
        </p>
      );
    }
    return null;
  };

  const renderPlanStatusLine = () => {
    if (pricingMode) {
      return (
        <p style={stylesText.metaLine}>
          Start free and upgrade only when you need more documents, contract reviews, storage, or
          Vision Assist.
        </p>
      );
    }

    if (plan_type === "trial_7d") {
      if (is_active) {
        return (
          <p style={stylesText.metaLine}>
            <strong>Status:</strong> 7-day trial active.
          </p>
        );
      }
      return (
        <p style={stylesText.metaLine}>
          <strong>Status:</strong> Trial expired – you can upgrade to a monthly plan or buy add-ons
          at any time.
        </p>
      );
    }

    if (isMonthlyCore) {
      if (is_active) {
        return (
          <p style={stylesText.metaLine}>
            <strong>Status:</strong> Monthly plan active{hasSubscription ? " (subscription)" : " (renews every 30 days)"}.
          </p>
        );
      }
      return (
        <p style={stylesText.metaLine}>
          <strong>Status:</strong> Monthly plan expired – you can still buy add-ons or start a new
          monthly plan.
        </p>
      );
    }

    if (isHiddenPlanType) {
      return (
        <p style={stylesText.metaLine}>
          <strong>Status:</strong> Active plan.
        </p>
      );
    }

    if (plan_type === "free") {
      return (
        <p style={stylesText.metaLine}>
          <strong>Status:</strong> Free plan.
        </p>
      );
    }

    return (
      <p style={stylesText.metaLine}>
        <strong>Status:</strong> Billing plan information unavailable.
      </p>
    );
  };

  const canShowMonthlyOptions = !monthlyPlanPurchaseLocked;
  const canShowCaseAnalyzerAddons = true;
  const canShowContractAddons = true;
  const canShowDocWriterAddons = !hasDocWriterUnlimited;

  const monthlyCards = useMemo(
    () => [
      {
        id: "monthly_essential",
        tag: "Starter",
        title: "Monthly Essential",
        price: "$69.99",
        priceNote: "Best for light monthly use",
        subtitle: "",
        bullets: [
          "Unlimited Legal Chat",
          "15 Doc Writer documents / month",
          "2 Contract Insight reports / month, up to 25 pages each",
          {
            title: "Document Intelligence",
            details: [
              "Up to 5 pages — Included",
              "6–100 pages — 4 analyses / month",
            ],
          },
          "Case Analyzer available as add-on",
          "10 minutes Vision Assist / month",
          `File Vault: ${STORAGE_CAPS.monthly_essential} included`,
        ],
        cta: "Start Essential",
        to: "/checkout?plan=monthly_essential",
        highlight: false,
        tone: "essential",
      },
      {
        id: "monthly_pro",
        tag: "Most Popular",
        title: "Monthly Pro",
        price: "$129.99",
        priceNote: "For professionals",
        subtitle: "",
        bullets: [
          "Unlimited Legal Chat",
          "40 Doc Writer documents / month",
          "4 Contract Insight reports / month, up to 25 pages each",
          {
            title: "Document Intelligence",
            details: [
              "Up to 5 pages — Included",
              "6–100 pages — 8 analyses / month",
            ],
          },
          "Case Analyzer available as add-on",
          "25 minutes Vision Assist / month",
          `File Vault: ${STORAGE_CAPS.monthly_pro} included`,
        ],
        cta: "Go Pro",
        to: "/checkout?plan=monthly_pro",
        highlight: true,
        tone: "pro",
      },
      {
        id: "monthly_elite",
        tag: "Best Value",
        title: "Monthly Elite",
        price: "$229.99",
        priceNote: "For power users & firms",
        subtitle: "",
        bullets: [
          "Unlimited Legal Chat",
          "Unlimited Doc Writer",
          "6 Contract Insight reports / month, up to 25 pages each",
          {
            title: "Document Intelligence",
            details: [
              "Up to 5 pages — Included",
              "6–100 pages — 12 analyses / month",
            ],
          },
          "1 Case Analyzer report / month, up to 250 pages",
          "45 minutes Vision Assist / month",
          `File Vault: ${STORAGE_CAPS.monthly_elite} included`,
          "Smart Alert included while plan is active",
        ],
        cta: "Choose Elite",
        to: "/checkout?plan=monthly_elite",
        highlight: false,
        tone: "elite",
      },
    ],
    []
  );

  const addOnCards = useMemo(
    () => [
      {
        group: "Case Analyzer",
        items: [
          {
            id: "ca_250",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 250,
            title: "Case Analyzer – up to 250 pages",
            price: "$49.99",
            price_cents: 4999,
            desc: "Starter case package with evidence review, probability profile, evidence map, and saved report.",
            show: canShowCaseAnalyzerAddons,
          },
          {
            id: "ca_500",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 500,
            title: "Case Analyzer – up to 500 pages",
            price: "$89.99",
            price_cents: 8999,
            desc: "Most common evidence package size for fuller case review and probability reporting.",
            show: canShowCaseAnalyzerAddons,
          },
          {
            id: "ca_750",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 750,
            title: "Case Analyzer – up to 750 pages",
            price: "$139.99",
            price_cents: 13999,
            desc: "Larger evidence package for multi-document matters and deeper case analysis.",
            show: canShowCaseAnalyzerAddons,
          },
          {
            id: "ca_1000",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 1000,
            title: "Case Analyzer – up to 1,000 pages",
            price: "$189.99",
            price_cents: 18999,
            desc: "Complex matter package for larger evidence sets, OCR-heavy files, and detailed reports.",
            show: canShowCaseAnalyzerAddons,
          },
          {
            id: "ca_1250",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 1250,
            title: "Case Analyzer – up to 1,250 pages",
            price: "$239.99",
            price_cents: 23999,
            desc: "Heavy document set package for extensive exhibits, records, and communications.",
            show: canShowCaseAnalyzerAddons,
          },
          {
            id: "ca_1500",
            product: "case_analyzer",
            kind: "addon",
            page_tier: 1500,
            title: "Case Analyzer – up to 1,500 pages",
            price: "$289.99",
            price_cents: 28999,
            desc: "Maximum package for the largest supported case evidence bundles.",
            show: canShowCaseAnalyzerAddons,
          },
        ],
      },
      {
        group: "Contract Insight",
        items: [
          {
            id: "ci_25",
            product: "contract_insight",
            kind: "addon",
            page_tier: 25,
            title: "Contract Insight – up to 25 pages",
            price: "$24.99",
            price_cents: 2499,
            desc: "Professional contract review for standard agreements with key terms, flags, and scoring.",
            show: canShowContractAddons,
          },
          {
            id: "ci_50",
            product: "contract_insight",
            kind: "addon",
            page_tier: 50,
            title: "Contract Insight – up to 50 pages",
            price: "$39.99",
            price_cents: 3999,
            desc: "Medium agreement review with deeper clause analysis and report output.",
            show: canShowContractAddons,
          },
          {
            id: "ci_75",
            product: "contract_insight",
            kind: "addon",
            page_tier: 75,
            title: "Contract Insight – up to 75 pages",
            price: "$59.99",
            price_cents: 5999,
            desc: "Professional tier for detailed business contracts and longer agreements.",
            show: canShowContractAddons,
          },
          {
            id: "ci_150",
            product: "contract_insight",
            kind: "addon",
            page_tier: 150,
            title: "Contract Insight – up to 150 pages",
            price: "$99.99",
            price_cents: 9999,
            desc: "Advanced review for complex agreements, schedules, and exhibit-heavy contracts.",
            show: canShowContractAddons,
          },
          {
            id: "ci_250",
            product: "contract_insight",
            kind: "addon",
            page_tier: 250,
            title: "Contract Insight – up to 250 pages",
            price: "$149.99",
            price_cents: 14999,
            desc: "Large contract package for heavy agreements and professional-grade reporting.",
            show: canShowContractAddons,
          },
        ],
      },
      {
        group: "Doc Writer",
        items: [
          {
            id: "dw_1",
            product: "doc_writer",
            kind: "addon",
            qty: 1,
            title: "Doc Writer – 1 Document",
            price: "$4.99",
            price_cents: 499,
            desc: "Draft or refine one legal-style document.",
            show: canShowDocWriterAddons,
          },
          {
            id: "dw_5",
            product: "doc_writer",
            kind: "addon",
            qty: 5,
            title: "Doc Writer – 5 Documents",
            price: "$17.99",
            price_cents: 1799,
            desc: "Lower per-document cost in a bundle.",
            show: canShowDocWriterAddons,
          },
          {
            id: "dw_15",
            product: "doc_writer",
            kind: "addon",
            qty: 15,
            title: "Doc Writer – 15 Documents",
            price: "$39.99",
            price_cents: 3999,
            desc: "Best value for frequent document drafting.",
            show: canShowDocWriterAddons,
          },
        ],
      },
      {
        group: "Smart Alert",
        items: [
          {
            id: "smart_alert_6",
            product: "smart_alert",
            kind: "addon",
            qty: 6,
            title: "Smart Alert – 6 Alerts",
            price: "$29.99",
            price_cents: 2999,
            desc: "Send urgent multi-channel alerts to trusted contacts.",
            show: canShowSmartAlertAddons,
          },
          {
            id: "smart_alert_12",
            product: "smart_alert",
            kind: "addon",
            qty: 12,
            title: "Smart Alert – 12 Alerts",
            price: "$39.99",
            price_cents: 3999,
            desc: "Double the capacity for ongoing protection.",
            show: canShowSmartAlertAddons,
          },
          {
            id: "smart_alert_24",
            product: "smart_alert",
            kind: "addon",
            qty: 24,
            title: "Smart Alert – 24 Alerts",
            price: "$69.99",
            price_cents: 6999,
            desc: "Best value for ongoing alert coverage.",
            show: canShowSmartAlertAddons,
          },
        ],
      },
      {
        group: "Vision Assist",
        items: [
          {
            id: "va_15",
            product: "vision_assist",
            kind: "addon",
            minutes: 15,
            title: "Vision Assist – 15 Minutes",
            price: "$14.99",
            price_cents: 1499,
            desc: "Screen-aware co-pilot to help you fill out forms step-by-step.",
            show: true,
          },
          {
            id: "va_30",
            product: "vision_assist",
            kind: "addon",
            minutes: 30,
            title: "Vision Assist – 30 Minutes",
            price: "$27.99",
            price_cents: 2799,
            desc: "Longer working session for multiple forms or documents.",
            show: true,
          },
          {
            id: "va_60",
            product: "vision_assist",
            kind: "addon",
            minutes: 60,
            title: "Vision Assist – 60 Minutes",
            price: "$49.99",
            price_cents: 4999,
            desc: "Deep-dive support for complex matters.",
            show: true,
          },
        ],
      },
    ],
    [canShowCaseAnalyzerAddons, canShowContractAddons, canShowDocWriterAddons, canShowSmartAlertAddons]
  );

  const activePill = useMemo(() => {
    if (pricingMode) return null;
    const label =
      plan_type === "trial_7d"
        ? "Trial"
        : isHiddenPlanType
        ? "Custom"
        : MONTHLY_LABELS[plan_type] || (plan_type ? String(plan_type) : "");
    const status = is_active ? "Active" : "Inactive";
    return `${label} • ${status}`;
  }, [pricingMode, plan_type, is_active, isHiddenPlanType]);

  const pageTitle = pricingMode ? "Pricing" : "Billing & Plans";

  const fetchSetupIntent = async () => {
    setSetupError("");
    setSetupClientSecret("");
    setSetupLoading(true);

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();

      const res = await fetch(`${HOST}/billing/setup-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.clientSecret) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setSetupClientSecret(data.clientSecret);
      setShowUpdateCard(true);
    } catch (e) {
      setSetupError(e?.message || "Unable to initialize card update.");
      setShowUpdateCard(true);
    } finally {
      setSetupLoading(false);
    }
  };

  const startStripeConnectOnboarding = async () => {
    setConnectSetupError("");
    setConnectSetupNotice("");

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      if (!influencerUid) throw new Error("Missing influencer account.");

      const token = await user.getIdToken();
      setConnectSetupLoading(true);

      const res = await fetch(`${HOST}/connect/account-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ influencer_uid: influencerUid }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      if (data?.connect_status) {
        setConnectStatus(data.connect_status);
      }

      setConnectSetupNotice("Redirecting you to Stripe onboarding…");
      window.location.assign(data.url);
    } catch (err) {
      console.error("Stripe Connect onboarding error:", err);
      setConnectSetupError(err?.message || "Unable to start Stripe onboarding right now.");
    } finally {
      setConnectSetupLoading(false);
    }
  };

  const setAutoRenew = async (enabled) => {
    setRenewError("");
    if (!hasSubscription) return;

    if (enabled === false) {
      const ok = window.confirm(
        `Turn off auto-renew?\n\nYou will keep access until ${renewalLabel}. We won't charge you again unless you turn it back on.`
      );
      if (!ok) return;
    }

    setRenewLoading(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();

      const res = await fetch(`${HOST}/billing/auto-renew`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: !!enabled }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      await refreshBilling();
    } catch (e) {
      setRenewError(e?.message || "Unable to update auto-renew.");
    } finally {
      setRenewLoading(false);
    }
  };

  const fetchBillingHistory = async ({ limit = 3, showAll = false } = {}) => {
    if (pricingMode) return;

    setBillingHistoryLoading(true);
    setBillingHistoryError("");

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();

      const params = new URLSearchParams();
      params.set("limit", String(limit || 3));
      if (showAll) {
        params.set("months", "24");
        params.set("all", "1");
      }

      const res = await fetch(`${HOST}/user/billing-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      setBillingHistory({ ...data, items });
      setBillingHistoryLimit(Number(data?.limit || limit || 3));
    } catch (err) {
      console.error("Billing history fetch error:", err);
      setBillingHistoryError(err?.message || "Unable to load billing history right now.");
    } finally {
      setBillingHistoryLoading(false);
    }
  };

  const toggleBillingHistory = async () => {
    if (pricingMode) return;

    if (showBillingHistory) {
      setShowBillingHistory(false);
      return;
    }

    setShowBillingHistory(true);
    if (!billingHistory?.items?.length) {
      await fetchBillingHistory({ limit: 3 });
    }
  };

  const loadMoreBillingHistory = async () => {
    const currentLimit = Number(billingHistory?.limit || billingHistoryLimit || 3);
    const shouldShowAll = currentLimit >= 12 && billingHistory?.show_all_available;
    const nextLimit = shouldShowAll ? 100 : Number(billingHistory?.next_limit || Math.min(currentLimit + 3, 12));
    await fetchBillingHistory({ limit: nextLimit, showAll: shouldShowAll });
  };

  const downloadCustomerBillingPdf = async (item) => {
    const fulfillmentId = item?.id || item?.fulfillment_id;
    if (!fulfillmentId || downloadingBillingId) return;

    setDownloadingBillingId(fulfillmentId);
    setBillingHistoryError("");

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();

      const res = await fetch(`${HOST}/user/billing-history/${encodeURIComponent(fulfillmentId)}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          message = errData?.error || message;
        } catch {
          // ignore json parsing for PDF errors
        }
        throw new Error(message);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const disposition = res.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const fallbackName = `AskLawly-Customer-Billing-Statement-${fulfillmentId}.pdf`;
      const downloadName = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1]) : fallbackName;

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Billing PDF download error:", err);
      setBillingHistoryError(err?.message || "Unable to download this billing statement right now.");
    } finally {
      setDownloadingBillingId("");
    }
  };

  const togglePackageRow = (key) => {
    setExpandedPackageRows((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  };

  const downloadInfluencerStatement = async (monthKey) => {
    if (!monthKey || downloadingStatementMonth) return;

    setStatementDownloadState({
      type: "loading",
      message: `Generating your ${formatMonthKey(monthKey)} statement PDF…`,
      month_key: monthKey,
    });
    setDownloadingStatementMonth(monthKey);

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();

      const res = await fetch(
        `${HOST}/user/influencer-earnings/statement-pdf?month_key=${encodeURIComponent(monthKey)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          message = errData?.error || message;
        } catch {
          // ignore json parsing
        }
        throw new Error(message);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const disposition = res.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const fallbackName = `AskLawly-Influencer-Earnings-Statement-${monthKey}.pdf`;
      const downloadName = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1]) : fallbackName;

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      setStatementDownloadState({
        type: "success",
        message: "Your statement download has started.",
        month_key: monthKey,
      });

      window.setTimeout(() => {
        setStatementDownloadState((prev) =>
          prev?.type === "success" && prev?.month_key === monthKey
            ? { type: "", message: "", month_key: "" }
            : prev
        );
      }, 3500);
    } catch (err) {
      console.error("Statement download error:", err);
      setStatementDownloadState({
        type: "error",
        message: err?.message || "Unable to generate your statement right now.",
        month_key: monthKey,
      });
    } finally {
      setDownloadingStatementMonth("");
    }
  };

  const monthlyPlanAllowances = {
    monthly_essential: { doc: 15, vision: 10, documentIntelligence: 4, storage: STORAGE_CAPS.monthly_essential },
    monthly_pro: { doc: 40, vision: 25, documentIntelligence: 8, storage: STORAGE_CAPS.monthly_pro },
    monthly_elite: { doc: null, vision: 45, documentIntelligence: 12, storage: STORAGE_CAPS.monthly_elite },
  };

  const currentAllowance = monthlyPlanAllowances[plan_type] || null;
  const docTotal = currentAllowance?.doc ?? null;
  const visionTotal = currentAllowance?.vision ?? null;
  const documentIntelligenceTotal = currentAllowance?.documentIntelligence ?? null;
  const monthlyDocWriterValue = doc_writer_unlimited
    ? "Unlimited"
    : docTotal != null
    ? `${monthlyDocWriterRemaining} / ${docTotal} remaining`
    : `${monthlyDocWriterRemaining} remaining`;
  const monthlyDocWriterPercent = doc_writer_unlimited ? 100 : docTotal ? (monthlyDocWriterRemaining / docTotal) * 100 : 0;
  const monthlyVisionValue = visionTotal != null
    ? `${formatVisionTime(monthlyVisionAssistRemaining)} / ${formatVisionTime(visionTotal)} remaining`
    : `${formatVisionTime(monthlyVisionAssistRemaining)} remaining`;
  const monthlyVisionPercent = visionTotal ? (monthlyVisionAssistRemaining / visionTotal) * 100 : 0;

  const documentIntelligenceValue =
    plan_type === "free"
      ? `${document_intelligence_lifetime_remaining} / ${document_intelligence_lifetime_limit} analyses remaining`
      : documentIntelligenceTotal != null
      ? `${monthlyDocumentIntelligenceRemaining} / ${documentIntelligenceTotal} analyses remaining`
      : `${monthlyDocumentIntelligenceRemaining} analyses remaining`;

  const documentIntelligenceNote =
    plan_type === "free"
      ? "Up to 5 pages per analysis"
      : "Up to 5 pages included; 6–100 pages use monthly analyses";

  const documentIntelligencePercent =
    plan_type === "free"
      ? document_intelligence_lifetime_limit
        ? (document_intelligence_lifetime_remaining / document_intelligence_lifetime_limit) * 100
        : 0
      : documentIntelligenceTotal
      ? (monthlyDocumentIntelligenceRemaining / documentIntelligenceTotal) * 100
      : 0;

  const legalChatValue = plan_type === "free"
    ? typeof remaining_questions === "number"
      ? `${remaining_questions} remaining`
      : "Limited"
    : "Unlimited";

  const addonCreditRows = [
    addonDocWriterRemaining > 0
      ? { type: "simple", label: "Doc Writer", value: `${addonDocWriterRemaining} remaining`, amount: addonDocWriterRemaining }
      : null,
    hasAddonContractInsightPackages
      ? {
          type: "packages",
          key: "addon_contract_insight",
          label: "Contract Insight",
          rows: addonContractInsightPackages,
          amount: addonContractInsightPackages.reduce((sum, row) => sum + Number(row.remaining || 0), 0),
        }
      : null,
    hasAddonCaseAnalyzerPackages
      ? {
          type: "packages",
          key: "addon_case_analyzer",
          label: "Case Analyzer",
          rows: addonCaseAnalyzerPackages,
          amount: addonCaseAnalyzerPackages.reduce((sum, row) => sum + Number(row.remaining || 0), 0),
        }
      : null,
    addonVisionAssistRemaining > 0
      ? { type: "simple", label: "Vision Assist", value: `${formatVisionTime(addonVisionAssistRemaining)} remaining`, amount: addonVisionAssistRemaining }
      : null,
    addonSmartAlertRemaining > 0
      ? { type: "simple", label: "Smart Alert", value: `${addonSmartAlertRemaining} remaining`, amount: addonSmartAlertRemaining }
      : null,
  ].filter(Boolean);

  const hasAddonCredits = addonCreditRows.length > 0;

  const legalChatPercent = plan_type === "free" ? 35 : 100;

  const visibleAddOnGroups = addOnCards
    .map((group) => ({ ...group, items: group.items.filter((item) => item.show) }))
    .filter((group) => group.items.length > 0);
  const selectedAddOnGroup =
    visibleAddOnGroups.find((group) => group.group === activeAddOnGroup) || visibleAddOnGroups[0] || null;

  const selectAddOnGroup = (groupName) => {
    setActiveAddOnGroup(groupName);

    if (!isSmallScreen) return;

    window.requestAnimationFrame?.(() => {
      addOnTabRefs.current?.[groupName]?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  };

  const handleAddOnToCart = (item) => {
    if (notAllowed && !pricingMode) return;

    if (pricingMode) {
      navigate("/login");
      return;
    }

    const cartItem = {
      id: item.id,
      product: item.product,
      plan: item.product,
      kind: item.kind || "addon",
      title: item.title,
      label: item.title,
      description: item.desc,
      page_tier: item.page_tier || null,
      pageTier: item.page_tier || null,
      qty: item.qty || null,
      minutes: item.minutes || null,
      price_cents: item.price_cents || priceToCents(item.price),
      priceCents: item.price_cents || priceToCents(item.price),
    };

    const alreadyInCart = isInCart(cartItem);
    if (!alreadyInCart) addToCart(cartItem);

    setCartNotice({
      title: item.title,
      message: alreadyInCart ? "This item is already in your cart." : "Added to cart.",
    });

    window.clearTimeout(window.__asklawlyCartNoticeTimer);
    window.__asklawlyCartNoticeTimer = window.setTimeout(() => {
      setCartNotice(null);
    }, 4500);
  };

  const renderCenteredState = (title, message) => (
    <div style={stylesUI.pageShell}>
      <div style={stylesUI.centerStateCard}>
        <div style={stylesUI.loadingMark} />
        <div style={stylesUI.centerStateTitle}>{title}</div>
        {!!message && <div style={stylesUI.centerStateText}>{message}</div>}
      </div>
    </div>
  );

  if (loading) return renderCenteredState("Loading billing", "Preparing your plan, payment, and usage details…");
  if (!billing && !pricingMode) return renderCenteredState("Unable to load billing data", "Please refresh the page and try again.");

  return (
    <div style={stylesUI.pageShell}>
      <div style={stylesUI.pageHeader}>
        <div>
          <h1 style={stylesText.h1}>{pricingMode ? "Plans & Pricing" : "Billing & Plans"}</h1>
        </div>

        <div style={stylesUI.headerActions}>
          {!pricingMode && cartCount > 0 ? (
            <button type="button" style={stylesUI.cartButton} onClick={() => navigate("/cart")}>
              <FiShoppingCart />
              <span>Cart</span>
              <span style={stylesUI.cartCountBadge}>{cartCount}</span>
            </button>
          ) : null}
        </div>
      </div>

      {pricingMode ? (
        <div style={{ ...stylesUI.freeNoteCard, ...(isSmallScreen ? stylesUI.freeNoteCardMobile : null) }}>
          <div style={stylesUI.freeNoteIcon}>i</div>
          <div>
            <div style={stylesUI.freeNoteTitle}>Start free, then upgrade only when needed.</div>
            <div style={stylesUI.freeNoteText}>
              Free access includes 1 Legal Chat session with up to 30 questions, 2 Document Intelligence analyses up to 5 pages each, {STORAGE_CAPS.free} File Vault storage, Case Value, and Case Estimator tools. Paid add-ons include Case Analyzer, Contract Insight, Doc Writer, Smart Alert, and Vision Assist.
            </div>
          </div>
          <button type="button" style={stylesUI.primaryBtn} onClick={() => navigate("/login")}>
            Sign in
          </button>
        </div>
      ) : (
        <div style={{ ...stylesUI.topSummaryGrid, ...(isSmallScreen ? stylesUI.topSummaryGridMobile : null) }}>
          <section style={stylesUI.planUsageComboPanel}>
            <div style={{ ...stylesUI.comboHeader, ...(isSmallScreen ? stylesUI.comboHeaderMobile : null) }}>
              <div style={stylesUI.comboPlanBlock}>
                <div style={{ ...stylesUI.comboPlanName, ...(isSmallScreen ? stylesUI.comboPlanNameMobile : null) }}>{prettyPlanType}</div>
                <div style={stylesUI.comboStatusRow}>
                  <span>Status</span>
                  <strong style={{ color: planStatusIsActive ? "#047857" : "#64748B" }}>
                    {planStatusLabel}
                  </strong>
                </div>
              </div>

              {showRenewalMeta ? (
                <div style={{ ...stylesUI.comboRenewalBlock, ...(isSmallScreen ? stylesUI.comboRenewalBlockMobile : null) }}>
                  <div style={{ ...stylesUI.comboMetaRow, ...(isSmallScreen ? stylesUI.comboMetaRowMobileLeft : null) }}>
                    <span>{isMonthlyCore && !is_active ? "Ended" : "Renews"}</span>
                    <strong>{renewalLabel}</strong>
                  </div>
                  <div style={{ ...stylesUI.comboMetaRow, ...(isSmallScreen ? stylesUI.comboMetaRowMobileRight : null) }}>
                    <span>Auto-renew:</span>
                    <strong style={{ color: autoRenewOn ? "#059669" : "#64748B" }}>
                      {hasSubscription ? (autoRenewOn ? "On" : "Off") : "—"}
                    </strong>
                  </div>
                </div>
              ) : null}
            </div>

            <div style={stylesUI.usageStack}>
              <UsageRow label="Legal Chat" value={legalChatValue} percent={legalChatPercent} unlimited={plan_type !== "free"} isSmallScreen={isSmallScreen} />
              <UsageRow
                label="Doc Writer"
                value={monthlyDocWriterValue}
                percent={monthlyDocWriterPercent}
                unlimited={doc_writer_unlimited}
                isSmallScreen={isSmallScreen}
              />
              <PackageUsageRow
                label="Contract Insight"
                rows={monthlyContractInsightPackages}
                expanded={!!expandedPackageRows.monthly_contract_insight}
                onToggle={() => togglePackageRow("monthly_contract_insight")}
                isSmallScreen={isSmallScreen}
              />
              <PackageUsageRow
                label="Case Analyzer"
                rows={monthlyCaseAnalyzerPackages}
                expanded={!!expandedPackageRows.monthly_case_analyzer}
                onToggle={() => togglePackageRow("monthly_case_analyzer")}
                isSmallScreen={isSmallScreen}
              />
              <UsageRow
                label="Document Intelligence"
                value={documentIntelligenceValue}
                percent={documentIntelligencePercent}
                note={documentIntelligenceNote}
                isSmallScreen={isSmallScreen}
              />
              <UsageRow
                label="Vision Assist"
                value={monthlyVisionValue}
                percent={monthlyVisionPercent}
                isSmallScreen={isSmallScreen}
              />
              {monthlySmartAlertRemaining > 0 || monthlyEntitlements?.smart_alert_included ? (
                <UsageRow
                  label="Smart Alert"
                  value={`${monthlySmartAlertRemaining} remaining`}
                  percent={monthlySmartAlertRemaining > 0 ? 100 : 0}
                  isSmallScreen={isSmallScreen}
                />
              ) : null}
              <UsageRow
                label="File Vault"
                value={`${storageUsed || "0 MB"} / ${storageTotal || (pricingMode ? STORAGE_CAPS.free : "N/A")}`}
                percent={effectiveQuotaLimit ? (effectiveQuotaUsed / effectiveQuotaLimit) * 100 : 0}
                isSmallScreen={isSmallScreen}
              />
            </div>
          </section>

          {hasAddonCredits ? (
            <section style={stylesUI.addonCreditsPanel}>
              <div style={stylesUI.addonCreditsTitle}>Add-on credits</div>
              <div style={stylesUI.addonCreditStack}>
                {addonCreditRows.map((row) =>
                  row.type === "packages" ? (
                    <CompactPackageUsageRow
                      key={row.key}
                      label={row.label}
                      rows={row.rows}
                      expanded={!!expandedPackageRows[row.key]}
                      onToggle={() => togglePackageRow(row.key)}
                      isSmallScreen={isSmallScreen}
                    />
                  ) : (
                    <CompactUsageRow key={row.label} label={row.label} value={row.value} isSmallScreen={isSmallScreen} />
                  )
                )}
              </div>
            </section>
          ) : null}

          <section style={stylesUI.summaryPanel}>
            <div style={stylesUI.cardIcon}>▣</div>
            <div style={stylesUI.summaryPanelBody}>
              <div style={stylesUI.summaryLabel}>Payment method</div>
              <div style={stylesUI.paymentText}>{paymentMethodLabel}</div>
              <div style={stylesUI.summaryMeta}>{card_last4 ? "Stored securely by Stripe." : "No card details available yet."}</div>
              <div style={stylesUI.cardActions}>
                <button
                  type="button"
                  style={{ ...stylesUI.primaryBtn, ...(setupLoading ? stylesUI.btnDisabled : null) }}
                  disabled={setupLoading}
                  onClick={fetchSetupIntent}
                >
                  {setupLoading ? "Preparing…" : "Update card"}
                </button>
                <button type="button" style={stylesUI.secondaryBtn} onClick={toggleBillingHistory}>
                  {showBillingHistory ? "Hide history" : "Billing history"}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {showBillingHistory && !pricingMode ? (
        <section style={stylesUI.billingHistoryPanel}>
          <div style={stylesUI.billingHistoryHeader}>
            <div>
              <div style={stylesUI.sectionTitle}>Billing history</div>
              <div style={stylesUI.sectionHint}>Recent verified purchases and subscription payments.</div>
            </div>
            <button type="button" style={stylesUI.iconCloseBtn} onClick={() => setShowBillingHistory(false)} aria-label="Close billing history">
              −
            </button>
          </div>

          {billingHistoryError ? (
            <div style={{ ...stylesUI.notice, color: "#B91C1C" }}>{billingHistoryError}</div>
          ) : null}

          {billingHistoryLoading && !billingHistory?.items?.length ? (
            <div style={stylesUI.notice}>Loading your billing history…</div>
          ) : billingHistory?.items?.length ? (
            isSmallScreen ? (
              <div style={stylesUI.billingHistoryMobileStack}>
                {billingHistory.items.map((item) => {
                  const rowId = item.id || item.fulfillment_id;
                  const isDownloading = downloadingBillingId === rowId;
                  return (
                    <div key={rowId} style={stylesUI.billingHistoryMobileCard}>
                      <div style={stylesUI.billingHistoryMobileTitle}>{item.display_title || item.description || "Purchase"}</div>
                      <div style={stylesUI.billingHistoryMobileDate}>{item.fulfilled_date_label || "—"}</div>
                      <div style={stylesUI.billingHistoryMobileMetaRow}>
                        <span>{String(item.status || "Paid").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}</span>
                        <strong>{item.amount_paid_label || "$0.00"}</strong>
                      </div>
                      <button
                        type="button"
                        style={{ ...stylesUI.downloadInvoiceLink, ...(isDownloading ? stylesUI.downloadInvoiceLinkDisabled : null) }}
                        disabled={isDownloading}
                        onClick={() => downloadCustomerBillingPdf(item)}
                      >
                        ↓ {isDownloading ? "Preparing PDF…" : "Download PDF"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={stylesUI.billingHistoryTableWrap}>
                <table style={stylesUI.billingHistoryTable}>
                  <thead>
                    <tr>
                      <th style={stylesUI.billingHistoryTh}>Date</th>
                      <th style={stylesUI.billingHistoryTh}>Description</th>
                      <th style={{ ...stylesUI.billingHistoryTh, textAlign: "right" }}>Amount</th>
                      <th style={stylesUI.billingHistoryTh}>Status</th>
                      <th style={{ ...stylesUI.billingHistoryTh, textAlign: "right" }}>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.items.map((item) => {
                      const rowId = item.id || item.fulfillment_id;
                      const isDownloading = downloadingBillingId === rowId;
                      return (
                        <tr key={rowId}>
                          <td style={stylesUI.billingHistoryTd}>{item.fulfilled_date_label || "—"}</td>
                          <td style={stylesUI.billingHistoryTdStrong}>{item.display_title || item.description || "Purchase"}</td>
                          <td style={{ ...stylesUI.billingHistoryTd, textAlign: "right", fontWeight: 900 }}>{item.amount_paid_label || "$0.00"}</td>
                          <td style={stylesUI.billingHistoryTd}>{String(item.status || "Paid").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}</td>
                          <td style={{ ...stylesUI.billingHistoryTd, textAlign: "right" }}>
                            <button
                              type="button"
                              style={{ ...stylesUI.downloadInvoiceLink, ...(isDownloading ? stylesUI.downloadInvoiceLinkDisabled : null) }}
                              disabled={isDownloading}
                              onClick={() => downloadCustomerBillingPdf(item)}
                            >
                              ↓ {isDownloading ? "Preparing…" : "Download PDF"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div style={stylesUI.notice}>No billing history yet. Verified purchases will appear here after payment is completed.</div>
          )}

          {billingHistory?.items?.length > 0 && (billingHistory?.has_more || billingHistory?.show_all_available) ? (
            <div style={stylesUI.billingHistoryFooter}>
              <button
                type="button"
                style={{ ...stylesUI.secondaryBtn, ...(billingHistoryLoading ? stylesUI.btnDisabledSecondary : null) }}
                disabled={billingHistoryLoading}
                onClick={loadMoreBillingHistory}
              >
                {billingHistoryLoading
                  ? "Loading…"
                  : Number(billingHistory?.limit || billingHistoryLimit) >= 12 && billingHistory?.show_all_available
                  ? "Show all from last 24 months"
                  : "Show more"}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {showUpdateCard && !pricingMode ? (
        <section style={stylesUI.sectionCard}>
          <div style={stylesUI.sectionHeaderPlain}>
            <div>
              <div style={stylesUI.sectionTitle}>Update your card</div>
              <div style={stylesUI.sectionHint}>Your card is processed securely by Stripe.</div>
            </div>
            <button
              type="button"
              style={stylesUI.secondaryBtn}
              onClick={() => {
                setShowUpdateCard(false);
                setSetupClientSecret("");
                setSetupError("");
              }}
            >
              Close
            </button>
          </div>

          {!!setupError && <div style={{ ...stylesUI.notice, color: "#B91C1C" }}>{setupError}</div>}

          {setupClientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret: setupClientSecret, appearance: { theme: "stripe" } }}>
              <UpdateCardForm
                onClose={async () => {
                  setShowUpdateCard(false);
                  setSetupClientSecret("");
                  setSetupError("");
                  await refreshBilling();
                }}
                onError={(msg) => setSetupError(msg)}
              />
            </Elements>
          ) : (
            <div style={stylesUI.notice}>{setupLoading ? "Loading secure form…" : "Unable to load the secure form. Please try again."}</div>
          )}
        </section>
      ) : null}

      <section id="plans_section" style={stylesUI.sectionBlock}>
        <div style={stylesUI.sectionHeaderPlain}>
          <div>
            <div style={stylesUI.sectionTitle}>{pricingMode ? "Choose a plan" : "Monthly plans"}</div>
          </div>
        </div>

        <div style={{ ...stylesUI.planGridClean, ...(isSmallScreen ? stylesUI.planGridMobile : null) }}>
          {monthlyCards.map((p) => {
            const isCurrent = !pricingMode && is_active && p.id === plan_type;
            const isBlockedByActivePlan = !pricingMode && monthlyPlanPurchaseLocked && !isCurrent;
            const disabled = Boolean(isCurrent || isBlockedByActivePlan || (Boolean(notAllowed) && !pricingMode));
            return (
              <PlanCard
                key={p.id}
                tag={p.tag}
                title={p.title}
                price={p.price}
                priceNote={p.priceNote}
                subtitle={p.subtitle}
                bullets={p.bullets}
                cta={
                  isCurrent
                    ? "Current Plan"
                    : isBlockedByActivePlan
                    ? "Unavailable while active"
                    : p.id === "monthly_essential"
                    ? "Choose Essential"
                    : p.id === "monthly_pro"
                    ? "Choose Pro"
                    : "Choose Elite"
                }
                highlight={p.highlight || isCurrent}
                tone={p.tone}
                isSmallScreen={isSmallScreen}
                current={isCurrent}
                disabled={disabled}
                onClick={() => {
                  if (isCurrent || (notAllowed && !pricingMode)) return;
                  if (isBlockedByActivePlan) {
                    window.alert(`You already have an active monthly plan until ${renewalLabel}. You can buy add-ons now, but monthly plan changes are unavailable until the current plan ends.`);
                    return;
                  }
                  if (pricingMode) {
                    navigate("/login");
                    return;
                  }
                  navigate(p.to);
                }}
              />
            );
          })}
        </div>
      </section>

      <section style={stylesUI.sectionBlock}>
        <div style={stylesUI.sectionHeaderPlain}>
          <div>
            <div style={stylesUI.sectionTitle}>Extras & add-ons</div>
            <div style={stylesUI.sectionHint}>No monthly plan required. Add credits only when you need them.</div>
          </div>
        </div>

        <div style={stylesUI.addonTabbedCard}>
          <div ref={addOnTabsRef} style={stylesUI.addonTabs}>
            {visibleAddOnGroups.map((group) => (
              <button
                key={group.group}
                ref={(node) => { addOnTabRefs.current[group.group] = node; }}
                type="button"
                style={{ ...stylesUI.addonTab, ...(selectedAddOnGroup?.group === group.group ? stylesUI.addonTabActive : null) }}
                onClick={() => selectAddOnGroup(group.group)}
              >
                {group.group}
              </button>
            ))}
          </div>

          {cartNotice ? (
            <div style={stylesUI.cartNotice}>
              <div>
                <strong>{cartNotice.message}</strong>
                <span>{cartNotice.title}</span>
              </div>
              <button type="button" style={stylesUI.cartNoticeBtn} onClick={() => navigate("/cart")}>
                View cart
              </button>
            </div>
          ) : null}

          {selectedAddOnGroup ? (
            <div style={{ ...stylesUI.addonGridClean, ...(isSmallScreen ? stylesUI.addonGridMobile : null) }}>
              {selectedAddOnGroup.items.map((it) => (
                <MiniCard
                  key={it.id}
                  title={it.title.replace(`${selectedAddOnGroup.group} – `, "")}
                  price={it.price}
                  desc={it.desc}
                  disabled={Boolean(notAllowed) && !pricingMode}
                  inCart={!pricingMode && isInCart({
                    id: it.id,
                    product: it.product,
                    price_cents: it.price_cents || priceToCents(it.price),
                    page_tier: it.page_tier || null,
                    qty: it.qty || null,
                    minutes: it.minutes || null,
                  })}
                  onClick={() => handleAddOnToCart(it)}
                  cta={pricingMode ? "Sign in to buy" : "Add to cart"}
                />
              ))}
            </div>
          ) : (
            <div style={stylesUI.notice}>No add-ons available right now.</div>
          )}
        </div>
      </section>

      {!pricingMode && isInfluencer ? (
        <section style={stylesUI.sectionBlock}>
          <div style={stylesUI.sectionHeaderPlain}>
            <div>
              <div style={stylesUI.sectionTitle}>Partner earnings</div>
              <div style={stylesUI.sectionHint}>Payout setup, promo code, monthly statements, and downloadable reports.</div>
            </div>
            {normalizedInfluencerBadge ? (
              <div
                style={{
                  ...stylesUI.pill,
                  ...(normalizedInfluencerBadge === "Active" ? stylesUI.statusPillActive : stylesUI.statusPillInactive),
                }}
              >
                {normalizedInfluencerBadge}
              </div>
            ) : null}
          </div>

          <div style={{ ...stylesUI.partnerGrid, ...(isSmallScreen ? stylesUI.partnerGridMobile : null) }}>
            <QuickMetric
              label="Current month"
              value={currentEarningsMonth ? formatMonthKey(currentEarningsMonth.month_key) : "—"}
              meta={currentEarningsMonth ? `${_safeCount(currentEarningsTotals.orders_count)} purchases • ${_safeCount(currentEarningsTotals.referral_uses)} referrals` : "No statement yet"}
            />
            <QuickMetric label="Pending" value={currentEarningsMonth ? formatMoney(currentEarningsTotals.commission_pending) : "—"} meta="Pending commission" />
            <QuickMetric label="Paid" value={currentEarningsMonth ? formatMoney(currentEarningsTotals.commission_paid) : "—"} meta="Paid this statement" />
            <QuickMetric label="Promo code" value={influencerPromoCode} meta="Active partner code" />
          </div>

          <div style={stylesUI.connectCard}>
            <div style={stylesUI.connectCardTop}>
              <div>
                <div style={stylesUI.connectTitle}>Stripe payout onboarding</div>
                <div style={stylesUI.connectDesc}>{stripeConnectDescription}</div>
              </div>
              <div
                style={{
                  ...stylesUI.pill,
                  ...(stripeConnectUiStatusRaw === "ready_for_payouts" || stripeConnectPayoutsEnabled
                    ? stylesUI.statusPillActive
                    : stylesUI.statusPillInactive),
                }}
              >
                {stripeConnectBadge}
              </div>
            </div>

            <div style={{ ...stylesUI.connectMetaRow, ...(isSmallScreen ? stylesUI.connectMetaRowMobile : null) }}>
              <div style={stylesUI.connectMetaItem}><span style={stylesUI.connectMetaLabel}>Account</span><span style={stylesUI.connectMetaValue}>{stripeConnectAccountId || "Will be created automatically"}</span></div>
              <div style={stylesUI.connectMetaItem}><span style={stylesUI.connectMetaLabel}>Details submitted</span><span style={stylesUI.connectMetaValue}>{stripeConnectDetailsSubmitted ? "Yes" : "No"}</span></div>
              <div style={stylesUI.connectMetaItem}><span style={stylesUI.connectMetaLabel}>Payouts</span><span style={stylesUI.connectMetaValue}>{stripeConnectPayoutsEnabled ? "Enabled" : "Not enabled"}</span></div>
              <div style={stylesUI.connectMetaItem}><span style={stylesUI.connectMetaLabel}>Charges</span><span style={stylesUI.connectMetaValue}>{stripeConnectChargesEnabled ? "Enabled" : "Not enabled"}</span></div>
            </div>

            {!!stripeConnectCurrentlyDue.length && <RequirementList title="Still required by Stripe" items={stripeConnectCurrentlyDue} />}
            {!!stripeConnectPastDue.length && <RequirementList title="Past due items" items={stripeConnectPastDue} />}
            {!!stripeConnectPendingVerification.length && <RequirementList title="Pending verification" items={stripeConnectPendingVerification} />}
            {!!stripeConnectEventuallyDue.length && <RequirementList title="Future items Stripe may request" items={stripeConnectEventuallyDue} />}
            {!!stripeConnectFutureCurrentlyDue.length && <RequirementList title="Upcoming required items" items={stripeConnectFutureCurrentlyDue} />}

            {(stripeConnectDisabledReason || stripeConnectLastSyncedAt) ? (
              <div style={{ ...stylesUI.connectMetaRow, ...(isSmallScreen ? stylesUI.connectMetaRowMobile : null) }}>
                {!!stripeConnectDisabledReason && (
                  <div style={stylesUI.connectMetaItem}>
                    <span style={stylesUI.connectMetaLabel}>Disabled reason</span>
                    <span style={stylesUI.connectMetaValue}>{stripeConnectDisabledReason.replace(/_/g, " ")}</span>
                  </div>
                )}
                {!!stripeConnectLastSyncedAt && (
                  <div style={stylesUI.connectMetaItem}>
                    <span style={stylesUI.connectMetaLabel}>Last synced</span>
                    <span style={stylesUI.connectMetaValue}>{new Date(stripeConnectLastSyncedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            ) : null}

            <div style={stylesUI.connectActionRow}>
              <button
                type="button"
                style={{ ...stylesUI.primaryBtn, ...((connectSetupLoading || connectStatusLoading) ? stylesUI.btnDisabled : null) }}
                disabled={connectSetupLoading || connectStatusLoading || !influencerUid}
                onClick={startStripeConnectOnboarding}
              >
                {connectSetupLoading ? "Preparing Stripe…" : stripeConnectActionLabel}
              </button>
              <button
                type="button"
                style={{ ...stylesUI.secondaryBtn, ...(connectStatusLoading ? stylesUI.btnDisabledSecondary : null) }}
                onClick={() => refreshStripeConnectStatus({ influencerUid, silent: false })}
                disabled={connectSetupLoading || connectStatusLoading}
              >
                {connectStatusLoading ? "Refreshing…" : "Refresh status"}
              </button>
            </div>

            {!!connectSetupNotice && <div style={{ ...stylesUI.notice, marginTop: 12 }}>{connectSetupNotice}</div>}
            {!!connectSetupError && <div style={{ ...stylesUI.notice, marginTop: 12, color: "#B91C1C" }}>{connectSetupError}</div>}
          </div>

          <div style={{ marginTop: 14 }}>
            {statementDownloadState?.message ? (
              <div
                style={{
                  ...stylesUI.statementToast,
                  ...(statementDownloadState.type === "error"
                    ? stylesUI.statementToastError
                    : statementDownloadState.type === "success"
                    ? stylesUI.statementToastSuccess
                    : stylesUI.statementToastLoading),
                }}
              >
                <div style={stylesUI.statementToastTitle}>
                  {statementDownloadState.type === "loading" ? "Preparing statement" : statementDownloadState.type === "success" ? "Download started" : "Unable to download"}
                </div>
                <div style={stylesUI.statementToastText}>{statementDownloadState.message}</div>
              </div>
            ) : null}

            {influencerLoading ? (
              <div style={stylesUI.notice}>Loading your partner earnings…</div>
            ) : influencerError ? (
              <div style={{ ...stylesUI.notice, color: "#B91C1C" }}>{influencerError}</div>
            ) : earningsMonths.length === 0 ? (
              <div style={stylesUI.notice}>No earnings statements yet. Once purchases start using your code, your monthly statement will appear here.</div>
            ) : (
              <div style={stylesUI.earningsStack}>
                {earningsMonths.map((month, index) => {
                  const totals = month?.totals || {};
                  const payout = month?.payout || {};
                  const breakdown = month?.breakdown || {};
                  const isExpanded = expandedStatementMonth === month?.month_key;
                  const isDownloading = downloadingStatementMonth === month?.month_key;
                  const breakdownRows = Object.entries(breakdown).filter(([, item]) => {
                    const purchaseCount = Number(item?.purchase_count || 0);
                    const earned = Number(item?.commission_earned || 0);
                    return purchaseCount > 0 || earned > 0;
                  });

                  return (
                    <div key={month?.month_key || `month-${index}`} style={stylesUI.earningsAccordionCard}>
                      <button
                        type="button"
                        style={stylesUI.earningsAccordionHeader}
                        onClick={() => setExpandedStatementMonth((prev) => (prev === month?.month_key ? "" : month?.month_key || ""))}
                        aria-expanded={isExpanded}
                      >
                        <div>
                          <div style={stylesUI.earningsMonthTitle}>{formatMonthKey(month?.month_key)}</div>
                          <div style={stylesUI.earningsMonthMeta}>{prettifyPayoutStatus(payout?.status)} • {month?.statement_status || "open"}</div>
                        </div>
                        <div style={stylesUI.earningsAccordionHeaderRight}>
                          <div style={stylesUI.earningsAccordionQuickStats}>
                            <span style={stylesUI.earningsAccordionQuickValue}>{formatMoney(totals.commission_total)}</span>
                            <span style={stylesUI.earningsAccordionQuickLabel}>total</span>
                          </div>
                          <div style={{ ...stylesUI.pill, ...(String(payout?.status || "").toLowerCase() === "paid" ? stylesUI.statusPillActive : stylesUI.statusPillInactive) }}>
                            {prettifyPayoutStatus(payout?.status)}
                          </div>
                          <div style={stylesUI.earningsAccordionChevron}>{isExpanded ? "−" : "+"}</div>
                        </div>
                      </button>

                      {isExpanded ? (
                        <div style={stylesUI.earningsAccordionBody}>
                          <div style={stylesUI.earningsAccordionActionRow}>
                            <button
                              type="button"
                              style={{ ...stylesUI.statementLinkBtn, ...(isDownloading ? stylesUI.statementLinkBtnDisabled : null) }}
                              disabled={isDownloading}
                              onClick={() => downloadInfluencerStatement(month?.month_key)}
                            >
                              {isDownloading ? "Generating statement…" : "Download statement"}
                            </button>
                          </div>

                          <div style={{ ...stylesUI.partnerGrid, ...(isSmallScreen ? stylesUI.partnerGridMobile : null) }}>
                            <QuickMetric label="Total" value={formatMoney(totals.commission_total)} meta="Total commission" />
                            <QuickMetric label="Pending" value={formatMoney(totals.commission_pending)} meta="Still unpaid" />
                            <QuickMetric label="Paid" value={formatMoney(totals.commission_paid)} meta="Already paid" />
                            <QuickMetric label="Purchases" value={_safeCount(totals.orders_count)} meta={`${_safeCount(totals.referral_uses)} total uses`} />
                          </div>

                          <div style={stylesUI.earningsSubTitle}>Breakdown</div>
                          {breakdownRows.length ? (
                            <div style={stylesUI.earningsTableWrap}>
                              <table style={stylesUI.earningsTable}>
                                <thead>
                                  <tr>
                                    <th style={stylesUI.earningsTh}>Service</th>
                                    <th style={stylesUI.earningsTh}>Count</th>
                                    <th style={stylesUI.earningsTh}>Rate</th>
                                    <th style={stylesUI.earningsTh}>Earned</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {breakdownRows.map(([key, item]) => (
                                    <tr key={key}>
                                      <td style={stylesUI.earningsTd}>{SERVICE_LABELS[key] || key}</td>
                                      <td style={stylesUI.earningsTd}>{_safeCount(item?.purchase_count)}</td>
                                      <td style={stylesUI.earningsTd}>{Number(item?.commission_rate || 0) > 0 ? `${Math.round(Number(item.commission_rate) * 100)}%` : "—"}</td>
                                      <td style={stylesUI.earningsTd}>{formatMoney(item?.commission_earned)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div style={stylesUI.notice}>No service breakdown yet for this month.</div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      ) : null}

    </div>
  );
};

/* ========================= Embedded Stripe Update Card Form ========================= */

const UpdateCardForm = ({ onClose, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const disabled = !stripe || !elements || saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: `${window.location.origin}/billing` },
        redirect: "if_required",
      });

      if (result?.error) {
        onError?.(result.error.message || "Unable to save card.");
        setSaving(false);
        return;
      }

      const pmId = result?.setupIntent?.payment_method;
      if (!pmId) {
        onError?.("Saved, but missing payment method ID. Please try again.");
        setSaving(false);
        return;
      }

      const user = getAuth().currentUser;
      if (!user) {
        onError?.("Not signed in.");
        setSaving(false);
        return;
      }

      const token = await user.getIdToken();
      const res = await fetch(`${HOST}/billing/set-default-payment-method`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ payment_method_id: pmId }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onError?.(data?.error || `HTTP ${res.status}`);
        setSaving(false);
        return;
      }

      onClose?.();
    } catch (err) {
      onError?.(err?.message || "Unexpected error saving card.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 10 }}>
      <div style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(2,6,23,0.12)", background: "#fff" }}>
        <PaymentElement />
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="submit" disabled={disabled} style={{ ...stylesUI.primaryBtn, ...(disabled ? stylesUI.btnDisabled : null) }}>
          {saving ? "Saving…" : "Save card"}
        </button>
        <button type="button" style={stylesUI.secondaryBtn} onClick={() => onClose?.()}>
          Done
        </button>
      </div>
    </form>
  );
};

/* ========================= UI Components ========================= */

const Badge = ({ text, compact = false, emphasis = false }) => (
  <span
    style={{
      ...stylesUI.badge,
      ...(compact ? stylesUI.badgeCompact : null),
      ...(emphasis ? stylesUI.badgeEmphasis : null),
    }}
  >
    {text}
  </span>
);

const SideItem = ({ title, desc }) => (
  <div style={stylesUI.sideItem}>
    <div style={stylesUI.sideItemTitle}>{title}</div>
    <div style={stylesUI.sideItemDesc}>{desc}</div>
  </div>
);

const FeatureCard = ({ emoji, title, desc }) => (
  <div style={stylesUI.featureCard}>
    <div style={stylesUI.featureEmoji}>{emoji}</div>
    <div style={stylesUI.featureTitle}>{title}</div>
    <div style={stylesUI.featureDesc}>{desc}</div>
  </div>
);

const WorkflowStep = ({ num, title, desc }) => (
  <div style={stylesUI.workflowStep}>
    <div style={stylesUI.workflowStepTop}>
      <div style={stylesUI.workflowNum}>{num}</div>
      <div>
        <div style={stylesUI.workflowStepTitle}>{title}</div>
        <div style={stylesUI.workflowStepDesc}>{desc}</div>
      </div>
    </div>
  </div>
);

const WorkflowConnector = () => <div style={stylesUI.workflowConnector} />;

const UsageRow = ({ label, value, percent = 0, unlimited = false, note = "", isSmallScreen = false }) => {
  const safePercent = Math.max(0, Math.min(100, Number(percent || 0)));
  return (
    <div style={{ ...stylesUI.usageRow, ...(isSmallScreen ? stylesUI.usageRowMobile : null) }}>
      <div style={{ ...stylesUI.usageLabel, ...(isSmallScreen ? stylesUI.usageLabelMobile : null) }}>{label}</div>
      <div style={{ ...stylesUI.usageBarTrack, ...(isSmallScreen ? stylesUI.usageBarTrackMobile : null) }}>
        <div style={{ ...stylesUI.usageBarFill, width: `${unlimited ? 100 : safePercent}%` }} />
      </div>
      <div
        style={{
          ...stylesUI.usageValue,
          ...(unlimited ? stylesUI.usageValueGreen : null),
          ...(isSmallScreen ? stylesUI.usageValueMobile : null),
        }}
      >
        {value}
        {!!note && <div style={stylesUI.usageValueNote}>{note}</div>}
      </div>
    </div>
  );
};

const PackageUsageRow = ({ label, rows = [], expanded = false, onToggle, isSmallScreen = false }) => (
  <div style={stylesUI.packageUsageShell}>
    <button
      type="button"
      style={{ ...stylesUI.packageToggleRow, ...(isSmallScreen ? stylesUI.packageToggleRowMobile : null) }}
      onClick={onToggle}
    >
      <span style={{ ...stylesUI.usageLabel, ...(isSmallScreen ? stylesUI.usageLabelMobile : null) }}>{label}</span>
      <span style={{ ...stylesUI.packageToggleSpacer, ...(isSmallScreen ? stylesUI.packageToggleSpacerMobile : null) }} />
      <span style={{ ...stylesUI.packageToggleAction, ...(isSmallScreen ? stylesUI.packageToggleActionMobile : null) }}>
        View packages {expanded ? "⌃" : "⌄"}
      </span>
    </button>
    {expanded ? <PackageDetailRows rows={rows} /> : null}
  </div>
);

const PackageDetailRows = ({ rows = [] }) => (
  <div style={stylesUI.packageDetailStack}>
    {rows.map((row) => (
      <div key={row.page_tier || row.label} style={stylesUI.packageDetailRow}>
        <span style={stylesUI.packageDetailLabel}>{row.label}</span>
        <strong style={stylesUI.packageDetailValue}>{row.value}</strong>
      </div>
    ))}
  </div>
);

const CompactPackageUsageRow = ({ label, rows = [], expanded = false, onToggle, isSmallScreen = false }) => (
  <div style={stylesUI.compactPackageShell}>
    <button
      type="button"
      style={{
        ...stylesUI.compactPackageToggleRow,
        ...(isSmallScreen ? stylesUI.compactPackageToggleRowMobile : null),
      }}
      onClick={onToggle}
    >
      <span style={stylesUI.compactUsageLabel}>{label}</span>
      <span style={{ ...stylesUI.compactPackageAction, ...(isSmallScreen ? stylesUI.compactPackageActionMobile : null) }}>
        View packages {expanded ? "⌃" : "⌄"}
      </span>
    </button>
    {expanded ? <PackageDetailRows rows={rows} /> : null}
  </div>
);

const CompactUsageRow = ({ label, value, isSmallScreen = false }) => (
  <div style={{ ...stylesUI.compactUsageRow, ...(isSmallScreen ? stylesUI.compactUsageRowMobile : null) }}>
    <div style={stylesUI.compactUsageLabel}>{label}</div>
    <div style={{ ...stylesUI.compactUsageBarTrack, ...(isSmallScreen ? stylesUI.compactUsageBarTrackMobile : null) }} />
    <div style={{ ...stylesUI.compactUsageValue, ...(isSmallScreen ? stylesUI.compactUsageValueMobile : null) }}>{value}</div>
  </div>
);

const QuickMetric = ({ label, value, meta }) => (
  <div style={stylesUI.metricCard}>
    <div style={stylesUI.summaryLabel}>{label}</div>
    <div style={stylesUI.summaryValue}>{value}</div>
    <div style={stylesUI.summaryMeta}>{meta}</div>
  </div>
);

const RequirementList = ({ title, items }) => (
  <div style={stylesUI.connectRequirementsWrap}>
    <div style={stylesUI.connectRequirementsTitle}>{title}</div>
    <div style={stylesUI.connectRequirementsList}>
      {items.map((item, idx) => (
        <span key={`${item}-${idx}`} style={stylesUI.connectRequirementPill}>
          {String(item).replace(/_/g, " ")}
        </span>
      ))}
    </div>
  </div>
);

const PlanCard = ({
  tag,
  title,
  price,
  priceNote,
  subtitle,
  bullets,
  cta,
  onClick,
  highlight,
  tone = "essential",
  isSmallScreen,
  disabled,
  current,
}) => {
  const icon = tone === "elite" ? "◇" : tone === "pro" ? "♕" : "✦";
  return (
    <div
      style={{
        ...stylesUI.planCard,
        ...(highlight ? stylesUI.planCardHighlight : null),
        ...(current ? stylesUI.planCardCurrent : null),
        ...(isSmallScreen ? stylesUI.planCardMobile : null),
      }}
    >
      {!!tag && <div style={{ ...stylesUI.planTag, ...(tone === "elite" ? stylesUI.planTagGreen : null) }}>{tag}</div>}
      <div style={stylesUI.planHeaderClean}>
        <div style={{ ...stylesUI.planIcon, ...(tone === "elite" ? stylesUI.planIconGreen : tone === "pro" ? stylesUI.planIconBlue : null) }}>{icon}</div>
        <div style={{ minWidth: 0 }}>
          <div style={stylesUI.planTitle}>{title}</div>
          {!!subtitle && <div style={stylesUI.planSubtitle}>{subtitle}</div>}
        </div>
      </div>
      <div style={stylesUI.planPriceRow}>
        <span style={stylesUI.planPrice}>{price}</span>
        <span style={stylesUI.planPriceSuffix}>/month</span>
      </div>
      {!!priceNote && <div style={stylesUI.planPriceNote}>{priceNote}</div>}
      <div style={stylesUI.planBullets}>
        {bullets.map((b, i) => {
          const isStructuredFeature = b && typeof b === "object" && !React.isValidElement(b);
          return (
            <div key={i} style={stylesUI.bulletRow}>
              <span style={stylesUI.bulletDot}>✓</span>
              {isStructuredFeature ? (
                <div style={stylesUI.bulletFeatureBlock}>
                  <div style={stylesUI.bulletFeatureTitle}>{b.title}</div>
                  {(b.details || []).map((detail, detailIndex) => (
                    <div key={`${i}-${detailIndex}`} style={stylesUI.bulletFeatureDetail}>
                      {detail}
                    </div>
                  ))}
                </div>
              ) : (
                <span style={stylesUI.bulletText}>{b}</span>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        style={{
          ...stylesUI.planButton,
          ...(highlight && !current ? stylesUI.planButtonPrimary : null),
          ...(current ? stylesUI.planButtonCurrent : null),
          ...(disabled && !current ? stylesUI.btnDisabledSecondary : null),
        }}
        onClick={onClick}
        disabled={!!disabled}
      >
        {cta}
      </button>
    </div>
  );
};

const MiniCard = ({ title, price, desc, onClick, cta = "Buy", disabled, inCart }) => (
  <div style={{ ...stylesUI.miniCard, ...(inCart ? stylesUI.miniCardInCart : null) }}>
    <div style={stylesUI.miniIcon}>⌕</div>
    <div style={stylesUI.miniContent}>
      <div style={stylesUI.miniTitle}>{title}</div>
      <div style={stylesUI.miniDesc}>{desc}</div>
      <div style={stylesUI.miniPrice}>{price}</div>
    </div>
    <button
      type="button"
      style={{ ...stylesUI.miniBtn, ...(inCart ? stylesUI.miniBtnInCart : null), ...(disabled ? stylesUI.btnDisabledSecondary : null) }}
      onClick={onClick}
      disabled={!!disabled}
    >
      {inCart ? "Already in cart" : cta}
    </button>
  </div>
);

const FaqItem = ({ q, a }) => (
  <div style={stylesUI.faqItem}>
    <div style={stylesUI.faqQ}>{q}</div>
    <div style={stylesUI.faqA}>{a}</div>
  </div>
);

function _safeCount(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

/* ========================= Styles ========================= */

const stylesText = {
  h1: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  metaLine: {
    margin: 0,
    color: "#64748B",
    fontSize: 13.5,
    lineHeight: 1.55,
  },
};

const stylesUI = {
  pageShell: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 40px",
    boxSizing: "border-box",
    background: "#F5F7FA",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  cartButton: {
    border: "1px solid rgba(37, 99, 235, 0.20)",
    background: "#17245F",
    color: "#FFFFFF",
    borderRadius: 999,
    padding: "9px 12px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
    boxShadow: "0 10px 22px rgba(23, 36, 95, 0.22)",
  },
  cartCountBadge: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 999,
    background: "#FFFFFF",
    color: "#17245F",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  headerKicker: {
    color: "#1E2A78",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  headerSub: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 720,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 999,
    background: "rgba(37, 99, 235, 0.08)",
    color: "#1E2A78",
    border: "1px solid rgba(37, 99, 235, 0.16)",
    fontSize: 12.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  statusPillActive: {
    background: "rgba(5, 150, 105, 0.10)",
    color: "#047857",
    borderColor: "rgba(5, 150, 105, 0.18)",
  },
  statusPillInactive: {
    background: "rgba(100, 116, 139, 0.10)",
    color: "#475569",
    borderColor: "rgba(100, 116, 139, 0.18)",
  },
  pageHero: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.25fr) minmax(300px, 0.75fr)",
    gap: 18,
    marginBottom: 18,
  },
  pageHeroMobile: {
    gridTemplateColumns: "1fr",
  },
  heroCard: {
    background: "linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)",
    border: "1px solid rgba(30, 42, 120, 0.10)",
    borderRadius: 24,
    padding: 24,
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    overflow: "hidden",
    position: "relative",
  },
  heroCardMobile: {
    padding: 18,
    textAlign: "center",
  },
  heroEyebrow: {
    color: "#1E2A78",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  heroTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 38,
    lineHeight: 1.05,
    fontWeight: 950,
    letterSpacing: "-1.2px",
    maxWidth: 720,
  },
  heroTitleMobile: {
    fontSize: 30,
    textAlign: "center",
  },
  heroSub: {
    margin: "12px 0 0",
    color: "#475569",
    fontSize: 15.5,
    lineHeight: 1.6,
    maxWidth: 720,
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18,
  },
  heroActionsMobile: {
    justifyContent: "center",
  },
  primaryBtn: {
    appearance: "none",
    border: "1px solid rgba(37, 99, 235, 0.22)",
    borderRadius: 12,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "10px 14px",
    fontSize: 13.5,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.18)",
  },
  secondaryBtn: {
    appearance: "none",
    border: "1px solid rgba(30, 42, 120, 0.20)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "10px 14px",
    fontSize: 13.5,
    fontWeight: 900,
    cursor: "pointer",
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  btnDisabledSecondary: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  heroSide: {
    borderRadius: 24,
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    padding: 18,
  },
  heroSideTitle: {
    fontSize: 14,
    fontWeight: 950,
    color: "#0F172A",
    marginBottom: 12,
  },
  sideItem: {
    borderRadius: 16,
    padding: 12,
    background: "#F8FAFC",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    marginBottom: 10,
  },
  sideItemTitle: {
    color: "#0F172A",
    fontSize: 13.5,
    fontWeight: 950,
  },
  sideItemDesc: {
    marginTop: 4,
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  freeNoteCard: {
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto",
    gap: 14,
    alignItems: "center",
    background: "#FFFFFF",
    border: "1px solid rgba(37, 99, 235, 0.14)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  },
  freeNoteCardMobile: {
    gridTemplateColumns: "1fr",
    textAlign: "center",
  },
  freeNoteIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(37, 99, 235, 0.09)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
  },
  freeNoteTitle: {
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  freeNoteText: {
    marginTop: 4,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.45,
  },
  topSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(270px, 0.75fr)",
    gap: 14,
    marginBottom: 18,
    alignItems: "start",
  },
  topSummaryGridMobile: {
    gridTemplateColumns: "1fr",
  },
  summaryPanel: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
    display: "flex",
    gap: 13,
    minWidth: 0,
  },
  summaryPanelWide: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
    minWidth: 0,
  },
  billingHistoryPanel: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
    marginTop: -4,
    marginBottom: 18,
    minWidth: 0,
  },
  billingHistoryHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  iconCloseBtn: {
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#F8FAFC",
    color: "#475569",
    cursor: "pointer",
    fontSize: 22,
    lineHeight: "28px",
    fontWeight: 950,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  billingHistoryTableWrap: {
    width: "100%",
    overflowX: "auto",
    borderRadius: 16,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
  },
  billingHistoryTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 720,
  },
  billingHistoryTh: {
    padding: "11px 12px",
    background: "#F8FAFC",
    color: "#475569",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    textAlign: "left",
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
    whiteSpace: "nowrap",
  },
  billingHistoryTd: {
    padding: "12px",
    color: "#475569",
    fontSize: 13,
    fontWeight: 750,
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  billingHistoryTdStrong: {
    padding: "12px",
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
    verticalAlign: "middle",
  },
  billingHistoryMobileStack: {
    display: "grid",
    gap: 10,
  },
  billingHistoryMobileCard: {
    borderRadius: 16,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#F8FAFC",
    padding: 13,
  },
  billingHistoryMobileTitle: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
    lineHeight: 1.3,
  },
  billingHistoryMobileDate: {
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 800,
    marginTop: 4,
  },
  billingHistoryMobileMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 12,
    color: "#475569",
    fontSize: 13,
    fontWeight: 900,
  },
  downloadInvoiceLink: {
    appearance: "none",
    border: "none",
    background: "transparent",
    color: "#2563EB",
    padding: 0,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
    textDecoration: "underline",
    textUnderlineOffset: 3,
    whiteSpace: "nowrap",
    marginTop: 10,
  },
  downloadInvoiceLinkDisabled: {
    color: "#94A3B8",
    cursor: "not-allowed",
  },
  billingHistoryFooter: {
    display: "flex",
    justifyContent: "center",
    marginTop: 14,
  },
  planUsageComboPanel: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
    minWidth: 0,
    gridColumn: "span 1",
  },
  comboHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(180px, auto)",
    gap: 16,
    alignItems: "start",
    marginBottom: 10,
  },
  comboHeaderMobile: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },
  comboPlanBlock: {
    minWidth: 0,
  },
  comboPlanName: {
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.45px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  comboPlanNameMobile: {
    fontSize: 20,
    lineHeight: 1.18,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    wordBreak: "normal",
  },
  comboStatusRow: {
    marginTop: 7,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 800,
  },
  comboRenewalBlock: {
    display: "grid",
    gap: 7,
    minWidth: 0,
    justifySelf: "end",
    textAlign: "right",
  },
  comboRenewalBlockMobile: {
    width: "100%",
    justifySelf: "stretch",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
  },
  comboMetaRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    justifyContent: "stretch",
    gap: 8,
    alignItems: "center",
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 800,
    minWidth: 0,
    width: "100%",
    textAlign: "right",
  },
  comboMetaRowMobileLeft: {
    gridTemplateColumns: "auto auto",
    justifyContent: "start",
    textAlign: "left",
    gap: 6,
  },
  comboMetaRowMobileRight: {
    gridTemplateColumns: "auto auto",
    justifyContent: "end",
    textAlign: "right",
    gap: 6,
  },
  addonCreditsPanel: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
    minWidth: 0,
  },
  addonCreditsTitle: {
    color: "#0F172A",
    fontSize: 20,
    fontWeight: 950,
    letterSpacing: "-0.35px",
    marginBottom: 14,
  },
  addonCreditStack: {
    display: "grid",
    gap: 12,
  },
  compactUsageRow: {
    display: "grid",
    gridTemplateColumns: "minmax(92px, 1fr) minmax(42px, 0.85fr) minmax(86px, auto)",
    alignItems: "center",
    gap: 9,
    minWidth: 0,
  },
  compactUsageRowMobile: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, auto)",
    gap: "6px 10px",
    alignItems: "start",
  },
  compactUsageLabel: {
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
    minWidth: 0,
  },
  compactUsageBarTrack: {
    height: 9,
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.08)",
    minWidth: 0,
  },
  compactUsageBarTrackMobile: {
    gridColumn: "1 / -1",
    gridRow: 2,
    width: "100%",
  },
  compactUsageValue: {
    textAlign: "right",
    color: "#475569",
    fontSize: 12.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  compactUsageValueMobile: {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  },
  summaryPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 12,
  },
  summaryPanelBody: {
    minWidth: 0,
    flex: 1,
  },
  cardIcon: {
    width: 42,
    height: 42,
    minWidth: 42,
    borderRadius: 14,
    background: "rgba(37, 99, 235, 0.09)",
    color: "#1E2A78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
  },
  summaryLabel: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  planName: {
    color: "#0F172A",
    fontSize: 21,
    fontWeight: 950,
    letterSpacing: "-0.4px",
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  paymentText: {
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    marginTop: 7,
  },
  summaryTitle: {
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    marginTop: 4,
  },
  smallMuted: {
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  summaryValue: {
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
    marginTop: 6,
  },
  summaryMeta: {
    marginTop: 8,
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  infoList: {
    marginTop: 12,
    display: "grid",
    gap: 7,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    color: "#64748B",
    fontSize: 12.5,
  },
  cardActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  usageStack: {
    display: "grid",
    gap: 10,
  },
  usageRow: {
    display: "grid",
    gridTemplateColumns: "130px minmax(90px, 1fr) minmax(96px, auto)",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  usageRowMobile: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, auto)",
    gap: "6px 10px",
    alignItems: "start",
  },
  usageLabel: {
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },
  usageLabelMobile: {
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  usageBarTrack: {
    height: 9,
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.08)",
    overflow: "hidden",
  },
  usageBarTrackMobile: {
    gridColumn: "1 / -1",
    gridRow: 2,
    width: "100%",
  },
  usageBarFill: {
    height: 9,
    borderRadius: 999,
    background: "#2563EB",
  },
  usageValue: {
    textAlign: "right",
    color: "#475569",
    fontSize: 12.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  usageValueMobile: {
    minWidth: 0,
    maxWidth: "100%",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    lineHeight: 1.3,
  },
  usageValueNote: {
    marginTop: 3,
    color: "#64748B",
    fontSize: 11.3,
    fontWeight: 750,
    lineHeight: 1.3,
    whiteSpace: "normal",
  },
  usageValueGreen: {
    color: "#047857",
  },
  packageUsageShell: {
    display: "grid",
    gap: 8,
    minWidth: 0,
  },
  packageToggleRow: {
    display: "grid",
    gridTemplateColumns: "130px minmax(90px, 1fr) minmax(116px, auto)",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    width: "100%",
    border: 0,
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    textAlign: "left",
  },
  packageToggleRowMobile: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, auto)",
    gap: "6px 10px",
    alignItems: "start",
  },
  packageToggleSpacer: {
    height: 9,
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.08)",
    minWidth: 0,
  },
  packageToggleSpacerMobile: {
    gridColumn: "1 / -1",
    gridRow: 2,
    width: "100%",
  },
  packageToggleAction: {
    textAlign: "right",
    color: "#1E2A78",
    fontSize: 12.5,
    fontWeight: 950,
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  packageToggleActionMobile: {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  },
  packageDetailStack: {
    display: "grid",
    gap: 6,
    padding: "8px 10px",
    marginLeft: 14,
    borderLeft: "2px solid rgba(37, 99, 235, 0.18)",
    background: "rgba(37, 99, 235, 0.035)",
    borderRadius: 12,
  },
  packageDetailRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
    minWidth: 0,
  },
  packageDetailLabel: {
    color: "#475569",
    fontSize: 12.2,
    fontWeight: 800,
    minWidth: 0,
  },
  packageDetailValue: {
    color: "#0F172A",
    fontSize: 12.2,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  compactPackageShell: {
    display: "grid",
    gap: 8,
    minWidth: 0,
  },
  compactPackageToggleRow: {
    display: "grid",
    gridTemplateColumns: "minmax(92px, 1fr) minmax(110px, auto)",
    alignItems: "center",
    gap: 9,
    minWidth: 0,
    width: "100%",
    border: 0,
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    textAlign: "left",
  },
  compactPackageToggleRowMobile: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, auto)",
    alignItems: "start",
  },
  compactPackageAction: {
    textAlign: "right",
    color: "#1E2A78",
    fontSize: 12.5,
    fontWeight: 950,
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  compactPackageActionMobile: {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  },
  sectionBlock: {
    marginTop: 18,
  },
  sectionCard: {
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 18,
    marginBottom: 18,
  },
  sectionHeaderPlain: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  sectionTitle: {
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.45px",
  },
  sectionHint: {
    marginTop: 5,
    color: "#64748B",
    fontSize: 13.5,
    lineHeight: 1.45,
  },
  planGridClean: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },
  planGridMobile: {
    gridTemplateColumns: "1fr",
  },
  planCard: {
    position: "relative",
    borderRadius: 22,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 18,
    minHeight: 460,
    display: "flex",
    flexDirection: "column",
  },
  planCardHighlight: {
    border: "1px solid rgba(37, 99, 235, 0.36)",
    boxShadow: "0 18px 45px rgba(37, 99, 235, 0.14)",
  },
  planCardCurrent: {
    outline: "2px solid rgba(5, 150, 105, 0.25)",
  },
  planCardMobile: {
    minHeight: "auto",
  },
  planTag: {
    position: "absolute",
    top: 14,
    right: 14,
    borderRadius: 999,
    padding: "5px 9px",
    background: "rgba(37, 99, 235, 0.09)",
    color: "#1E2A78",
    fontSize: 11.5,
    fontWeight: 950,
  },
  planTagGreen: {
    background: "rgba(5, 150, 105, 0.10)",
    color: "#047857",
  },
  planHeaderClean: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    paddingRight: 80,
  },
  planIcon: {
    width: 42,
    height: 42,
    minWidth: 42,
    borderRadius: 14,
    background: "rgba(30, 42, 120, 0.08)",
    color: "#1E2A78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    fontSize: 20,
  },
  planIconBlue: {
    background: "rgba(37, 99, 235, 0.10)",
    color: "#2563EB",
  },
  planIconGreen: {
    background: "rgba(5, 150, 105, 0.10)",
    color: "#047857",
  },
  planTitle: {
    color: "#0F172A",
    fontSize: 19,
    fontWeight: 950,
    letterSpacing: "-0.35px",
  },
  planSubtitle: {
    marginTop: 5,
    color: "#64748B",
    fontSize: 13,
    lineHeight: 1.4,
  },
  planPriceRow: {
    marginTop: 20,
    display: "flex",
    alignItems: "baseline",
    gap: 5,
  },
  planPrice: {
    color: "#0F172A",
    fontSize: 34,
    fontWeight: 950,
    letterSpacing: "-1px",
  },
  planPriceSuffix: {
    color: "#64748B",
    fontSize: 13.5,
    fontWeight: 800,
  },
  planPriceNote: {
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 700,
    marginTop: 3,
  },
  planBullets: {
    display: "grid",
    gap: 10,
    marginTop: 18,
    flex: 1,
  },
  bulletRow: {
    display: "grid",
    gridTemplateColumns: "20px minmax(0, 1fr)",
    gap: 8,
    alignItems: "start",
  },
  bulletDot: {
    width: 20,
    height: 20,
    borderRadius: 999,
    background: "rgba(5, 150, 105, 0.10)",
    color: "#047857",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  bulletText: {
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  bulletFeatureBlock: {
    minWidth: 0,
  },
  bulletFeatureTitle: {
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.35,
    fontWeight: 900,
  },
  bulletFeatureDetail: {
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.4,
    fontWeight: 750,
    marginTop: 2,
  },
  planButton: {
    marginTop: 18,
    appearance: "none",
    border: "1px solid rgba(30, 42, 120, 0.20)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "11px 14px",
    fontSize: 13.5,
    fontWeight: 950,
    cursor: "pointer",
    width: "100%",
  },
  planButtonPrimary: {
    background: "#2563EB",
    color: "#FFFFFF",
    borderColor: "rgba(37, 99, 235, 0.22)",
    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.18)",
  },
  planButtonCurrent: {
    background: "rgba(5, 150, 105, 0.10)",
    color: "#047857",
    borderColor: "rgba(5, 150, 105, 0.18)",
    cursor: "default",
  },
  addonTabbedCard: {
    borderRadius: 20,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  },
  addonTabs: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 10,
    overflowX: "auto",
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
  },
  addonTab: {
    appearance: "none",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 999,
    background: "#F8FAFC",
    color: "#475569",
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  addonTabActive: {
    background: "rgba(37, 99, 235, 0.10)",
    color: "#1E2A78",
    borderColor: "rgba(37, 99, 235, 0.20)",
  },
  cartNotice: {
    margin: "12px 14px 0",
    borderRadius: 14,
    border: "1px solid rgba(5, 150, 105, 0.22)",
    background: "rgba(5, 150, 105, 0.08)",
    color: "#065F46",
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    fontSize: 13,
  },
  cartNoticeBtn: {
    border: "1px solid rgba(5, 150, 105, 0.25)",
    background: "#FFFFFF",
    color: "#047857",
    borderRadius: 999,
    padding: "8px 11px",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12.5,
  },
  addonGridClean: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    padding: 14,
  },
  addonGridMobile: {
    gridTemplateColumns: "1fr",
  },
  miniCard: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#F8FAFC",
    padding: 14,
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr)",
    gap: 12,
    minHeight: 190,
  },
  miniCardInCart: {
    borderColor: "rgba(5, 150, 105, 0.26)",
    background: "rgba(240, 253, 244, 0.75)",
  },
  miniIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(37, 99, 235, 0.09)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    fontSize: 20,
  },
  miniContent: {
    minWidth: 0,
  },
  miniTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
  },
  miniDesc: {
    marginTop: 6,
    color: "#64748B",
    fontSize: 12.8,
    lineHeight: 1.45,
    minHeight: 38,
  },
  miniPrice: {
    marginTop: 12,
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.5px",
  },
  miniInCartText: {
    marginTop: 8,
    color: "#047857",
    fontSize: 12.5,
    fontWeight: 900,
  },
  miniBtn: {
    gridColumn: "1 / -1",
    appearance: "none",
    border: "1px solid rgba(37, 99, 235, 0.20)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: 13.5,
    fontWeight: 950,
    marginTop: 4,
  },
  miniBtnInCart: {
    borderColor: "rgba(5, 150, 105, 0.25)",
    background: "rgba(5, 150, 105, 0.08)",
    color: "#047857",
  },
  partnerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },
  partnerGridMobile: {
    gridTemplateColumns: "1fr",
  },
  metricCard: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 15,
  },
  connectCard: {
    marginTop: 14,
    borderRadius: 20,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 16,
  },
  connectCardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  connectTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
  },
  connectDesc: {
    color: "#64748B",
    marginTop: 5,
    fontSize: 13.2,
    lineHeight: 1.5,
    maxWidth: 760,
  },
  connectMetaRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
    marginTop: 14,
  },
  connectMetaRowMobile: {
    gridTemplateColumns: "1fr",
  },
  connectMetaItem: {
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    padding: 11,
    minWidth: 0,
  },
  connectMetaLabel: {
    display: "block",
    color: "#64748B",
    fontSize: 11.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  connectMetaValue: {
    display: "block",
    marginTop: 5,
    color: "#0F172A",
    fontSize: 12.5,
    fontWeight: 850,
    overflowWrap: "anywhere",
  },
  connectRequirementsWrap: {
    marginTop: 14,
    borderRadius: 14,
    border: "1px solid rgba(245, 158, 11, 0.18)",
    background: "rgba(245, 158, 11, 0.07)",
    padding: 12,
  },
  connectRequirementsTitle: {
    color: "#92400E",
    fontSize: 12.5,
    fontWeight: 950,
    marginBottom: 8,
  },
  connectRequirementsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  connectRequirementPill: {
    borderRadius: 999,
    padding: "5px 8px",
    background: "#FFFFFF",
    border: "1px solid rgba(245, 158, 11, 0.22)",
    color: "#92400E",
    fontSize: 11.5,
    fontWeight: 850,
  },
  connectActionRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  notice: {
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    padding: 12,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  earningsStack: {
    display: "grid",
    gap: 12,
  },
  earningsAccordionCard: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  },
  earningsAccordionHeader: {
    width: "100%",
    appearance: "none",
    border: "none",
    background: "#FFFFFF",
    padding: 15,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    textAlign: "left",
  },
  earningsMonthTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
  },
  earningsMonthMeta: {
    color: "#64748B",
    fontSize: 12.5,
    marginTop: 4,
    fontWeight: 700,
  },
  earningsAccordionHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  earningsAccordionQuickStats: {
    textAlign: "right",
  },
  earningsAccordionQuickValue: {
    display: "block",
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 15,
  },
  earningsAccordionQuickLabel: {
    display: "block",
    color: "#64748B",
    fontSize: 11.5,
    fontWeight: 800,
  },
  earningsAccordionChevron: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "#F8FAFC",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0F172A",
    fontWeight: 950,
  },
  earningsAccordionBody: {
    borderTop: "1px solid rgba(15, 23, 42, 0.08)",
    padding: 15,
    background: "#F8FAFC",
  },
  earningsAccordionActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 12,
  },
  statementLinkBtn: {
    appearance: "none",
    border: "1px solid rgba(37, 99, 235, 0.20)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "9px 12px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },
  statementLinkBtnDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  statementToast: {
    borderRadius: 16,
    padding: 13,
    marginBottom: 12,
    border: "1px solid rgba(15, 23, 42, 0.08)",
  },
  statementToastLoading: {
    background: "rgba(37, 99, 235, 0.07)",
    color: "#1E2A78",
  },
  statementToastSuccess: {
    background: "rgba(5, 150, 105, 0.08)",
    color: "#047857",
  },
  statementToastError: {
    background: "rgba(185, 28, 28, 0.07)",
    color: "#B91C1C",
  },
  statementToastTitle: {
    fontWeight: 950,
    fontSize: 13.5,
  },
  statementToastText: {
    marginTop: 4,
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  earningsSubTitle: {
    margin: "16px 0 8px",
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  earningsTableWrap: {
    overflowX: "auto",
    background: "#FFFFFF",
    borderRadius: 14,
    border: "1px solid rgba(15, 23, 42, 0.08)",
  },
  earningsTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 520,
  },
  earningsTh: {
    textAlign: "left",
    padding: "10px 11px",
    color: "#475569",
    fontSize: 12,
    fontWeight: 950,
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
  },
  earningsTd: {
    padding: "10px 11px",
    color: "#0F172A",
    fontSize: 12.5,
    fontWeight: 750,
    borderBottom: "1px solid rgba(15, 23, 42, 0.05)",
  },
  centerStateCard: {
    maxWidth: 420,
    margin: "80px auto",
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 20,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    padding: 24,
    textAlign: "center",
  },
  loadingMark: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: "4px solid rgba(37, 99, 235, 0.12)",
    borderTopColor: "#2563EB",
    margin: "0 auto 14px",
  },
  centerStateTitle: {
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
  },
  centerStateText: {
    color: "#64748B",
    fontSize: 13.5,
    lineHeight: 1.5,
    marginTop: 6,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    background: "rgba(30, 42, 120, 0.08)",
    color: "#1E2A78",
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 900,
  },
  badgeCompact: {
    padding: "4px 8px",
    fontSize: 11.5,
  },
  badgeEmphasis: {
    background: "rgba(37, 99, 235, 0.10)",
    color: "#2563EB",
  },
  featureCard: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    padding: 14,
  },
  featureEmoji: {
    fontSize: 22,
    marginBottom: 8,
  },
  featureTitle: {
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  featureDesc: {
    color: "#64748B",
    fontSize: 12.8,
    lineHeight: 1.45,
    marginTop: 5,
  },
  workflowStep: {
    borderRadius: 16,
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    padding: 12,
  },
  workflowStepTop: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  workflowNum: {
    width: 26,
    height: 26,
    borderRadius: 999,
    background: "rgba(37, 99, 235, 0.09)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  workflowStepTitle: {
    color: "#0F172A",
    fontSize: 13.5,
    fontWeight: 950,
  },
  workflowStepDesc: {
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.45,
    marginTop: 4,
  },
  workflowConnector: {
    height: 1,
    background: "rgba(15, 23, 42, 0.08)",
    margin: "8px 0",
  },
  faqItem: {
    borderRadius: 16,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#FFFFFF",
    padding: 14,
  },
  faqQ: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  faqA: {
    color: "#64748B",
    fontSize: 12.8,
    lineHeight: 1.5,
    marginTop: 6,
  },
};

export default Billing;
